import React, { useRef, useState, useEffect } from 'react';

import moment from 'moment-timezone';
import update from 'immutability-helper';

import API from 'files/api.js';
import Abstract from 'classes/Abstract.js';
import { AddEditEvent, AddEditEventType, EventDetails, EventTypeDetails, getEventStatus } from 'managers/Clients.js';
import { AddEditLead, OutboundCallDetails, LeadDetails, SetLeadStatus } from 'managers/Leads.js';
import AltFieldMapper from 'views/AltFieldMapper.js';
import Appearance from 'styles/Appearance.js';
import BookAppointment from 'views/BookAppointment.js';
import BoolToggle from 'views/BoolToggle.js';
import OutboundCall from 'classes/OutboundCall.js';
import Cookies from 'js-cookie';
import Content from 'managers/Content.js';
import DateDurationPickerField from 'views/DateDurationPickerField.js';
import DatePickerField from 'views/DatePickerField.js';
import DatePickerDropdownField from 'views/DatePickerDropdownField.js';
import Appointment from 'classes/Appointment.js';
import AppointmentLookupField from 'views/AppointmentLookupField.js';
import EditTarget from 'views/EditTarget.js';
import Event from 'classes/Event.js';
import Feedback from 'classes/Feedback.js';
import FieldMapper, { formatFields } from 'views/FieldMapper.js';
import ImagePickerField from 'views/ImagePickerField.js';
import { JitsiMeeting as JitsiMeetingComponent } from '@jitsi/react-sdk';
import Layer, { LayerItem, LayerItemSpacing } from 'structure/Layer.js';
import Lead from 'classes/Lead.js';
import LottieView from 'views/Lottie.js';
import MultipleListField from 'views/MultipleListField.js';
import Panel from 'structure/Panel.js';
import PatternPickerField, { convertPattern } from 'views/PatternPickerField.js';
import QuestionEditor from 'views/QuestionEditor.js';
import PageControl from 'views/PageControl.js';
import PermissionsContainer from 'views/PermissionsContainer.js';
import ProgressBar from 'views/ProgressBar.js';
import Request from 'files/Request.js';
import Scheduler, { SchedulerEventHeight } from 'views/Scheduler.js';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import StatusCodeFilters from 'views/StatusCodeFilters.js';
import SystemEvent from 'classes/SystemEvent.js';
import { SystemEventsLayerItem, UserDetails, reassignTasks, unassignTasks } from 'managers/Users.js';
import TableListHeader from 'views/TableListHeader.js';
import TextField from 'views/TextField.js';
import TextView from 'views/TextView.js';
import TimezoneLookupField from 'views/TimezoneLookupField.js';
import User from 'classes/User.js';
import UserLookupField from 'views/UserLookupField.js';
import Utils from 'files/Utils.js';
import Views, { AltBadge } from 'views/Main.js';
import { WeekCalendar } from 'views/Calendar.js';

export const AppointmentsCalendar = ({ index, options, utils }) => {

    const panelID = 'appointments_calendar';
    const limit = 15;

    const contentContainer = useRef(null);
    const offset = useRef(0);
    const showInactive = useRef(false);

    const [calendarStyle, setCalendarStyle] = useState('timeGrid');
    const [categories, setCategories] = useState([]);
    const [categoryItems, setCategoryItems] = useState([]);
    const [client, setClient] = useState(utils.client.get().id);
    const [events, setEvents] = useState([]);
    const [filters, setFilters] = useState(Object.values(Appointment.status.get()));
    const [hiddenUsers, setHiddenUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [managingUsers, setManagingUsers] = useState(false);
    const [operatingHours, setOperatingHours] = useState(Utils.getDefaultOperatingHours());
    const [paging, setPaging] = useState(null);
    const [rescheduleContext, setRescheduleContext] = useState(null);
    const [targetDate, setTargetDate] = useState(moment());
    const [timezone, setTimezone] = useState(utils.user.get().timezone);
    const [timelineEvents, setTimelineEvents] = useState([]);
    const [users, setUsers] = useState([]);
    const [userStates, setUserStates] = useState([]);

    const onAddNewEventType = () => {

        // prepare new event type
        let target = Event.Type.new();
        target.client_id = utils.client.get().id;

        // show new event type editing layer
        utils.layer.open({
            id: 'new_event_type',
            abstract: Abstract.create({
                object: target,
                type: 'event_type'
            }),
            Component: AddEditEventType.bind(this, { 
                isNewTarget: true,
                onChange: type => {

                    // prepare list of ids for local storage cache
                    let ids = categoryItems.map(selection => selection.id).concat([type.id]);
                    let key = getLocalStorageKey();
                    localStorage.setItem(key, JSON.stringify(ids));

                    // update list of selected event types
                    setCategories(ids);
                }
            })
        });
    }

    const setEventLoading = abstract => {
        setLoading(`${abstract.type}_${abstract.object.id}`);
    }

    const onCategoriesChange = items => {

        // prepare formatted list of selected categories
        let selections = categoryItems.filter(category => {
            return items.find(item => item.id === category.id) ? true : false;
        });

        // prepare list of ids for local storage cache
        let ids = selections.map(selection => selection.id);
        let key = getLocalStorageKey();
        localStorage.setItem(key, JSON.stringify(ids));

        // update state with new selections
        setCategories(ids);
    }

    const onContentUpdate = abstract => {
        setEvents(events => {
            return events.map(evt => {

                // determine if a status change was posted
                if(['appointment_status'].includes(abstract.type) === true) {
                    switch(abstract.type) {
                        case 'appointment_status':
                        if(evt.type === 'appointments' && evt.id === abstract.object.id) {
                            return update(evt, {
                                extendedProps: {
                                    event: {
                                        object: {
                                            $apply: object => {
                                                object.status = abstract.object.status;
                                                return object;
                                            }
                                        }
                                    }
                                },
                                status: {
                                    $set: abstract.object.status
                                }
                            });
                        }
                        break;
                    }
                }

                // no additional logic is required for event non-matches
                if(`${evt.type}-${evt.id}` !== abstract.getTag()) {
                    return evt;
                }

                // format event data
                return {
                    end: moment.utc(abstract.object.end_date).local().format('YYYY-MM-DD HH:mm:ss'),
                    eventResourceEditable: true,
                    eventStartEditable: true,
                    extendedProps: {
                        event: formatAbstractEvent(abstract)
                    },
                    id: abstract.object.id,
                    start: moment.utc(abstract.object.start_date).local().format('YYYY-MM-DD HH:mm:ss'),
                    status: abstract.object.status,
                    type: abstract.type
                }
            });
        });
    }

    const onCustomEventRender = target => {

        // prepare event variables
        let { appointment, end_date, lead, start_date, status, title, type } = target.object;

        // prepare quick look items
        let quickLook = [{
            key: 'time',
            value: `${moment(start_date).format('h:mma')} to ${moment(end_date).format('h:mma')}`
        },{
            key: 'appointments',
            value: appointment && (
                <>
                <span>{`Appointment: `}</span>
                <span style={{
                    textDecoration: 'underline'
                }}>{appointment.full_name}</span>
                </>
            )
        },{
            key: 'lead',
            value: lead && (
                <>
                <span>{`Lead: `}</span>
                <span style={{
                    textDecoration: 'underline'
                }}>{lead.full_name}</span>
                </>
            )
        }];

        // prepare name label for event, status code color, and abstract loading tag
        let color = status && status.color || Appearance.colors.grey();
        let tag = `${target.type}_${target.object.id}`;

        return (
            <div
            className={`text-button mb-1`}
            onClick={onEventClick.bind(this, target)}
            title={title}
            style={{
                background: Appearance.colors.softGradient(color),
                border: `2px solid ${color}`,
                borderRadius: 5,
                height: '100%',
                overflow: 'hidden',
                width: '100%'
            }}>
                <div style={{
                    alignItems: 'center',
                    borderBottom: `${loading === tag ? 0 : 2}px solid ${color}`,
                    display: 'flex',
                    flexDirection: 'row',
                    padding: '8px 12px 8px 12px',
                    position: 'relative'
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minWidth: 0
                    }}>
                        <span
                        className={'d-block w-100'}
                        style={{
                            color: 'white',
                            fontSize: 12,
                            fontWeight: 700,
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>{title}</span>
                        <span
                        className={'d-block w-100'}
                        style={{
                            color: 'white',
                            fontSize: 12,
                            fontWeight: 600,
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textDecoration: 'underline',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>{status.text}</span>
                    </div>
                    <img
                    src={type.icon.url}
                    style={{
                        border: '2px solid white',
                        borderRadius: 12.5,
                        height: 25,
                        marginLeft: 8,
                        minHeight: 25,
                        minWidth: 25,
                        overflow: 'hidden',
                        width: 25
                    }} />
                </div>
                {loading === tag && (
                    <div style={{
                        height: 2,
                        overflow: 'hidden',
                        width: '100%'
                    }}>
                        <ProgressBar 
                        barColor={'#FFFFFF'} 
                        trackColor={status.color}/>
                    </div>
                )}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '8px 12px 8px 12px'
                }}>
                    {quickLook.map(item => {
                        return (
                            <span
                            key={item.key}
                            className={'d-block w-100'}
                            style={{
                                color: 'white',
                                fontSize: 11,
                                fontWeight: 600,
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}>{item.value}</span>
                        )
                    })}
                </div>
            </div>
        )
    }

    const onClientChange = () => {
        setClient(utils.client.get().id);
        fetchEvents();
    }

    const onAppointmentEventRender = target => {

        // prepare event variables and date formatting options
        let { end_date, first_name, full_name, last_name, lead_type, spouse_first_name, start_date, status } = target.object;
        let options = { exclude_date: true, include_timezone: true, timezone, utc: true };

        // prepare quick look items
        let quickLook = [{
            key: 'time',
            value: `${Utils.formatDate(start_date, options)} to ${Utils.formatDate(end_date, options)}`
        }];

        // add assigned user to quick look if applicable
        let user = target.object.user_id && users.find(user => user.user_id === target.object.user_id);
        if(user) {
            /*
            quickLook.push({
                key: 'assigned',
                value: `Assigned to ${user.full_name}`
            });
            */
        }

        // prepare name label for event, status code color, optional reschedule context, and abstract loading tag
        let color = status && status.color || Appearance.colors.grey();
        let name = full_name ? `${first_name}${spouse_first_name ? ` & ${spouse_first_name}` : ''} ${last_name || ''}` : 'Customer name not available';
        let rescheduleContext = getRescheduleContext(target);
        let tag = `${target.type}_${target.object.id}`;

        return (
            <div
            className={`text-button mb-1`}
            onClick={onEventClick.bind(this, target)}
            title={name}
            style={{
                background: Appearance.colors.softGradient(color),
                border: `2px solid ${color}`,
                borderRadius: 5,
                height: '100%',
                overflow: 'hidden',
                width: '100%'
            }}>
                <div style={{
                    alignItems: 'center',
                    borderBottom: `${loading === tag ? 0 : 2}px solid ${color}`,
                    display: 'flex',
                    flexDirection: 'row',
                    padding: '8px 12px 8px 12px',
                    position: 'relative'
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minWidth: 0
                    }}>
                        <span style={{
                            color: 'white',
                            fontSize: 12,
                            fontWeight: 700,
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>{name}</span>
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-start',
                            maxWidth: '100%',
                            minWidth: 0
                        }}>
                            <span
                            className={'cursor-pointer'}
                            onClick={onStatusClick.bind(this, target)}
                            style={{
                                color: 'white',
                                fontSize: 12,
                                fontWeight: 600,
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textDecoration: 'underline',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}>{status.text}</span>
                        </div>
                    </div>
                    {lead_type && lead_type.icon && (
                        <img
                        src={lead_type.icon.url}
                        style={{
                            border: '2px solid white',
                            borderRadius: 15,
                            flex: 'none',
                            height: 30,
                            marginLeft: 8,
                            overflow: 'hidden',
                            width: 30
                        }} />
                    )}
                </div>
                {loading === tag && (
                    <div style={{
                        height: 2,
                        overflow: 'hidden',
                        width: '100%'
                    }}>
                        <ProgressBar barColor={'#FFFFFF'} trackColor={status.color}/>
                    </div>
                )}
                {rescheduleContext && status.code === Appointment.status.get().rescheduled && (
                    <div style={{
                        borderBottom: `2px solid ${color}`,
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '8px 12px 8px 12px'
                    }}>
                        <span 
                        className={'d-block w-100'}
                        style={{
                            color: 'white',
                            fontSize: 11,
                            fontWeight: 600,
                            whiteSpace: 'normal'
                        }}>{rescheduleContext.message}</span>
                    </div>
                )}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '8px 12px 8px 12px'
                }}>
                    {quickLook.map(item => {
                        return (
                            <span
                            key={item.key}
                            className={'d-block w-100'}
                            style={{
                                color: 'white',
                                fontSize: 11,
                                fontWeight: 600,
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}>{item.value}</span>
                        )
                    })}
                </div>
            </div>
        )
    }

    const onDownloadCalendar = async () => {
        return new Promise(async (resolve, reject) => {
            try {
                let { url } = await Request.get(utils, '/appointments/', {
                    type: 'download_calendar'
                });
                resolve(url);
            } catch(e) {
                reject(e);
            }
        });
    }

    const onEventClick = async abstract => {
        try {

            // start loading for abstract target
            setEventLoading(abstract);

            // fetch details for appointment if applicable
            if(abstract.type === 'appointments') {
                let appointment = await Appointment.get(utils, abstract.object.id);
                setLoading(false);
                utils.layer.open({
                    abstract: Abstract.create({
                        object: appointment,
                        type: 'appointments'
                    }),
                    Component: AppointmentDetails,
                    id: `appointment_details_${appointment.id}`,
                    permissions: ['appointments.details']
                });
                return;
            }

            // fetch details for event if applicable
            if(abstract.type === 'events') {
                let event = await Event.get(utils, abstract.object.id);
                setLoading(false);
                utils.layer.open({
                    id: `event_details_${event.id}`,
                    abstract: Abstract.create({
                        object: event,
                        type: 'events'
                    }),
                    Component: EventDetails
                });
                return;
            }

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the details for this event. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onEventDrop = props => {

        // prevent appointment rescheduling for user accounts not found in the array
        let { admin, primary, setter } = User.levels.get();
        if([admin, primary, setter].includes(utils.user.get().level) === false) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'It looks like your account is unable to reschedule appointments. Please speak with your primary or setter if you need to make changes to the time and date for this appointment.'
            });
            return;
        }

        // prevent reschduling if the selected day has not changed
        let { delta, oldEvent } = props;
        if(delta.days == 0) {
            console.log('no scheduling changes have been made');
            return;
        }

        // prepare new event start date
        let endDate = moment(oldEvent.end).add(delta.days, 'days');
        let startDate = moment(oldEvent.start).add(delta.days, 'days');
        let duration = moment.duration(endDate.diff(startDate)).asSeconds();

        // temporarily move event to new timeslot while user inputs are being requested
        setEvents(events => events.map(evt => {
            if(evt.extendedProps.event.object.id === oldEvent.extendedProps.event.object.id && evt.extendedProps.event.type === oldEvent.extendedProps.event.type) {
                evt.restore = { ...evt };
                evt.start = startDate.format('YYYY-MM-DD HH:mm:ss');
                evt.end = endDate.format('YYYY-MM-DD HH:mm:ss');
            }
            return evt;
        }));
        
        // request confirmation for rescheduling if target is a custom event
        if(oldEvent.extendedProps.type === 'events') {
            utils.alert.show({
                title: 'Reschedule Appointment',
                message: `Are you sure that you want to reschedule this event for ${Utils.formatDate(startDate, { include_timezone: true, timezone: timezone })}?`,
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
    
                    // determine if the event can be rescheduled
                    if(key === 'confirm') {
                        onRescheduleEvent({
                            duration: duration,
                            evt: oldEvent,
                            start_date: startDate
                        });
                        return;
                    }
    
                    // revert temporary event changes if the above logic was not triggered
                    setEvents(events => events.map(evt => evt.restore || evt));
                }
            });
            return;
        }

        // request confirmation for rescheduling with reason if target is a appointment
        let reason = null;
        utils.alert.show({
            title: 'Reschedule Appointment',
            message: `Are you sure that you want to reschedule this appointment for ${Utils.formatDate(startDate, { include_timezone: true, timezone: timezone })}? Please provide your reason for rescheduling below.`,
            content: (
                <div style={{
                    paddingLeft: 12,
                    paddingRight: 12,
                    width: '100%'
                }}>
                    <TextView
                    placeholder={'Reason for Rescheduling'}
                    onChange={text => reason = text} />
                </div>
            ),
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {

                // determine if the appointment can be rescheduled
                if(reason && key === 'confirm') {
                    onRescheduleAppointment({
                        duration: duration,
                        evt: oldEvent,
                        reason: reason,
                        start_date: startDate
                    });
                    return;
                }

                // revert temporary event changes if the above logic was not triggered
                setEvents(events => events.map(evt => evt.restore || evt));
            }
        });
    }

    const onEventRender = target => {

        // determine which type of event needs to be rendered
        switch(target.type) {
            case 'appointments':
            return onAppointmentEventRender(target);

            case 'events':
            return onCustomEventRender(target);

            default:
            return null;
        }
    }

    const onEventTypeClick = index => {
        let type = categoryItems[index];
        utils.layer.open({
            id: `event_type_details_${type.id}`,
            abstract: Abstract.create({
                object: type,
                type: 'event_type'
            }),
            Component: EventTypeDetails
        });
    }

    const onNewEventClick = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: Event.new(),
                type: 'events'
            }),
            Component: AddEditEvent.bind(this, { isNewTarget: true }),
            id: 'new_event',
            permissions: ['events.actions.new']
        });
    }

    const onRenderCallout = ({ evt }) => {

        let options = { include_timezone: true, timezone: evt.abstract.object.timezone };
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                maxWidth: 350
            }}>
                <span style={{
                    ...Appearance.textStyles.title()
                }}>{`${Utils.formatDate(evt.start, options).format('h:mma')} to ${Utils.formatDate(evt.end, options).format('h:mma')}`}</span>
                <span style={{
                    ...Appearance.textStyles.subTitle()
                }}>{Utils.formatAddress(evt.abstract.object.lead.address)}</span>
            </div>
        )
    }

    const onRenderEvent = (evt, props = {}) => {

        let { abstract, color, end, show_dates, start } = evt;
        let options = { exclude_date: true, include_timezone: true, timezone, utc: true };

        // return a loading component if abstract target is currently loading
        if(loading === `${abstract.type}_${abstract.object.id}`) {
            return (
                <div
                {...props}
                style={{
                    alignItems: 'center',
                    background: Appearance.colors.softGradient(color),
                    border: `2px solid ${color}`,
                    borderRadius: 10,
                    display: 'flex',
                    flexDirection: 'row',
                    height: SchedulerEventHeight,
                    justifyContent: 'center',
                    overflow: 'hidden',
                    paddingLeft: 24,
                    paddingRight: 24,
                    ...props.style
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={require('files/lottie/dots-white.json')}
                    style={{
                        height: 40,
                        width: 40
                    }}/>
                </div>
            )   
        }

        // fallback to returning a standard abstract component
        return (
            <div
            {...props}
            style={{
                alignItems: 'center',
                background: Appearance.colors.softGradient(color),
                border: `2px solid ${color}`,
                borderRadius: 10,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                overflow: 'hidden',
                position: 'relative',
                ...props.style
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: SchedulerEventHeight - 4, // subtract top and bottom border width
                    justifyContent: 'center',
                    width: '100%',
                }}>
                    {getEventTitle(evt, props)}
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        color: 'white',
                        display: 'block',
                        overflow: 'hidden',
                        paddingLeft: 12,
                        paddingRight: 12,
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>{`${abstract.object.status.text}${show_dates ? `: ${Utils.formatDate(start, options)} to ${Utils.formatDate(end, options)}` : ''}`}</span>
                </div>
                {getRescheduledAccessoryIcon()}
            </div>
        )
    }

    const onOptionsClick = evt => {
        utils.sheet.show({
            items: [{
                key: 'download',
                permissions: ['events.actions.download'],
                style: 'default',
                title: 'Download'
            },{
                key: 'manage_users',
                style: 'default',
                title: 'Manage Users'
            },{
                key: 'new_event',
                permissions: ['events.actions.new'],
                style: 'default',
                title: 'New Event'
            },{
                key: 'active_users',
                style: 'default',
                title: `${showInactive.current ? 'Hide' : 'Show'} Inactive Users`,
                visible: calendarStyle === 'timeGrid'
            },{
                key: 'sync',
                style: 'default',
                title: 'Sync with External Calendar'
            }],
            position: 'bottom',
            target: evt.target
        }, key => {
            if(key === 'active_users') {
                offset.current = 0;
                showInactive.current = !showInactive.current;
                fetchEvents();
                return;
            }
            if(key === 'download') {
                onDownloadCalendar();
                return;
            }
            if(key === 'manage_users') {
                offset.current = 0;
                setManagingUsers(true);
                return;
            }
            if(key === 'new_event') {
                onNewEventClick();
                return;
            }
            if(key === 'sync') {
                onSyncWithExternalCalendar();
                return;
            }
        })
    }

    const onRemoveEventAssignment = async evt => {
        utils.alert.show({
            title: 'Remove Assignment',
            message: 'Are you sure you want to unassign this event?',
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'canel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onRemoveEventAssignmentConfirm(evt);
                    return;
                }
            }
        });
    }

    const onRemoveEventAssignmentConfirm = async evt => {
        try {

            setEventLoading(evt.abstract);
            await Request.post(utils, '/events/', {
                id: evt.abstract.object.id,
                primary_user_id: null,
                type: 'set_assignment'
            });

            // end loading and update events
            setLoading(false);
            setEvents(events => {
                return events.map(prev_evt => {
                    if(prev_evt.id === evt.id) {
                        prev_evt.extendedProps.event.object.primary_user_id = null;
                    }
                    return prev_evt;
                });
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue removing the assignment from this event. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onRenderResource = resource => {
        return (
            <div 
            className={isUserInAppointment(resource.user_id) ? 'pinstripes in_appointment' : ''}
            style={{
                width: '100%'
            }}>
                {Views.entry({
                    bottomBorder: false,
                    hoverClassName: isUserInAppointment(resource.user_id) ? 'view-entry in_appointment' : null,
                    icon: {
                        path: resource.avatar,
                        imageStyle: {
                            boxShadow: null
                        }
                    },
                    loading: loading === `user_${resource.user_id}`,
                    onClick: onUserClick.bind(this, resource.user_id),
                    rightContent: managingUsers === true && (
                        <img 
                        className={'text-button'}
                        onClick={onUserVisibilityClick.bind(this, resource.user_id)}
                        src={hiddenUsers.includes(resource.user_id) ? 'images/non-visible-icon-grey.png' : 'images/visible-icon-grey.png'}
                        style={{
                            height: 25,
                            marginLeft: 8,
                            objectFit: 'contain',
                            width: 25
                        }} />
                    ),
                    subTitle: resource.phone_number,
                    title: resource.full_name
                })}
            </div>
        )
    }

    const onRescheduleContextClick = (text, evt) => {
        evt.stopPropagation();
        utils.alert.show({
            title: 'Reschedule Reason',
            message: text
        });
    }

    const onRescheduleAppointment = async ({ duration, evt, reason, start_date }) => {
        try {
            setEventLoading(evt.extendedProps.event);
            let { context } = await Request.post(utils, '/appointments/', {
                duration: duration,
                id: evt.id,
                reason: reason,
                start_date: start_date.utc().format('YYYY-MM-DD HH:mm:ss'),
                timezone: evt.extendedProps.event.object.timezone,
                type: 'reschedule'
            });

            // end loading and update reschedule context
            setLoading(false);
            setRescheduleContext(entries => {
                let tag = `${evt.extendedProps.event.type}_${evt.extendedProps.event.object.id}`;
                return update(entries, {
                    [tag]: {
                        $set: context
                    }
                });
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue rescheduling this appointment. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onRescheduleEvent = async ({ duration, evt, start_date }) => {
        try {
            setEventLoading(evt.extendedProps.event);
            await Request.post(utils, '/events/', {
                duration: duration,
                id: evt.id,
                primary_user_id: evt.resource_id,
                start_date: start_date.utc().format('YYYY-MM-DD HH:mm:ss'),
                type: 'reschedule'
            });

            // end loading
            setLoading(false);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue rescheduling this event. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onSetUserAppointmentState = async (userID, val) => {
        try {
            setLoading(`user_${userID}`);
            let { state } = await Request.post(utils, '/appointments/', {
                state: val,
                type: 'set_user_state',
                user_id: userID
            });

            setLoading(false);
            setUserStates(states => {
                states[userID] = state;
                return states;
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the appointment state for this user. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onShowUserDetails = async userID => {
        try {
            let user = await User.get(utils, userID);
            utils.layer.open({
                abstract: Abstract.create({
                    object: user,
                    type: 'users'
                }),
                Component: UserDetails,
                id: `user_details_${userID}`,
                permissions: ['users.details']
            })
        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the information for this account. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onStatusClick = async (target, evt) => {
        try {

            // stop click propagation from triggering parent click
            evt.stopPropagation();

            // fetch details for appointment
            setEventLoading(target);
            let appointment = await Appointment.get(utils, target.object.id);

            // end loading and show set status layer
            setLoading(false);
            utils.layer.open({
                id: `set_appointment_status_${appointment.id}`,
                abstract: Abstract.create({
                    object: appointment,
                    type: 'appointments'
                }),
                Component: SetAppointmentStatus
            });
            
        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the details for this appointment. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onSyncWithExternalCalendar = () => {

        // determine if client has permissions to use this feature
        if(utils.subscriptions.capabilities.get('global_data.calendars.external_sync') === false) {
            utils.subscriptions.capabilities.reject();
            return;
        }

        // show options to prepare external calendar documentation
        utils.alert.show({
            title: 'Sync with External Calendar',
            message: 'When enabled, we can automatically sync your appointments, appointment requests, and custom calendar events with a nubmer of external calendar apps. Select a calendar app below to get started.',
            buttons: [{
                key: 'ical',
                title: 'Apple iCal',
                style: 'default'
            },{
                key: 'google',
                title: 'Google Calendar',
                style: 'default'
            },{
                key: 'outlook',
                title: 'Microsoft Outlook',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key !== 'cancel') {
                    onSyncWithExternalCalendarConfirm(key);
                    return;
                }
            }
        });
    }

    const onSyncWithExternalCalendarConfirm = app => {

        let client = utils.client.get();

        // create temporary text area and add to body 
        let textArea = document.createElement('textarea');
        textArea.value = `${API.server}/system/calendars/feeds/${client.id}/${utils.user.get().user_id}/${client.public_auth_token}`;
        textArea.style.opacity = 0;
        textArea.style.position = 'fixed';
        document.body.appendChild(textArea);

        // focus and select textarea
        textArea.focus();
        textArea.select();

        // copy contents of textarea to clipboard
        navigator.clipboard.writeText(textArea.value);
        document.body.removeChild(textArea);
        
        // show confirmation alert with details for setting up subscription
        setTimeout(() => {
            utils.alert.show({
                title: 'All Done!',
                message: `We've copied a link to your clipboard that you can use to setup a calendar subscription. Click the "Learn More" button below to see the steps involved for setting up your calendar app subscription.`,
                buttons: [{
                    key: 'learn_more',
                    title: 'Learn More',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Done',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'learn_more') {
                        switch(app) {
                            case 'ical':
                            window.open('https://support.apple.com/guide/iphone/use-multiple-calendars-iph3d1110d4/ios');
                            break;
    
                            case 'google':
                            window.open('https://support.google.com/calendar/answer/37100');
                            break;
    
                            case 'outlook':
                            window.open('https://support.microsoft.com/en-us/office/import-or-subscribe-to-a-calendar-in-outlook-com-or-outlook-on-the-web-cff1429c-5af6-41ec-a5b4-74f2c278e98c');
                            break;
                        }
                    }
                }
            });
        }, 250);
    }

    const onTimelineEventClick = (evt, mouseEvent) => {

        // determine if an assignent has been made for a custom event
        if(evt.abstract.type === 'events' && evt.abstract.object.primary_user_id) {
            utils.sheet.show({
                items: [{
                    key: 'assignment',
                    title: 'Remove Assignment',
                    style: 'destructive'
                },{
                    key: 'details',
                    title: 'View Details',
                    style: 'default'
                }],
                position: 'bottom',
                target: mouseEvent.target
            }, key => {
                if(key === 'assignment') {
                    onRemoveEventAssignment(evt);
                    return;
                }
                if(key === 'details') {
                    onEventClick(evt.abstract);
                    return;
                }
            });
            return;
        }

        // trigger an event click for appointments and appointment requests
        onEventClick(evt.abstract);
    }

    const onUpdateTimelineEvent = ({ next_evt, prev_evt }) => {

        let options = { 
            exclude_date: true,  
            include_timezone: true, 
            timezone: prev_evt.abstract.object.timezone 
        };

        // prevent appointment updates for user accounts not found in the array
        let { admin, primary, setter } = User.levels.get();
        if([admin, primary, setter].includes(utils.user.get().level) === false) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'It looks like your account is unable to update appointments. Please speak with your primary or setter if you need to make changes to this appointment.'
            });
            return;
        }

        // declare rescheduling flag and loading flag
        setLoading(prev_evt.id);
        let isRescheduling = next_evt.start !== prev_evt.start || next_evt.end !== prev_evt.end;

        // temporarily move event to new timeslot while user inputs are being requested
        if(isRescheduling) {
            setEvents(events => events.map(evt => {
                if(evt.id === prev_evt.id) {
                    return {
                        ...evt,
                        end: moment(next_evt.end).format('YYYY-MM-DD HH:mm:ss'),
                        resource_id: next_evt.resource_id,
                        restore: { ...evt },
                        start: moment(next_evt.start).format('YYYY-MM-DD HH:mm:ss'),
                    }
                }
                return evt;
            }));
        }

        // request confirmation if changing assignment
        if(next_evt.resource_id !== prev_evt.resource_id) {

            // declare user for resource
            let resource = users.find(resource => resource.user_id === next_evt.resource_id);

            // derermine if a custom event is being assigned and request confirmation to assign
            if(prev_evt.abstract.type === 'events') {
                utils.alert.show({
                    title: 'Assign Event',
                    message: `Are you sure that you want to assign this event to ${resource.full_name}? This event will be scheduled for ${Utils.formatDate(next_evt.start, options)} to ${Utils.formatDate(next_evt.end, options)}.`,
                    buttons: [{
                        key: 'confirm',
                        title: 'Yes',
                        style: 'default'
                    },{
                        key: 'abort',
                        title: 'Cancel',
                        style: 'cancel'
                    }],
                    onClick: key => {
    
                        // determine if a standard event update is needed
                        if(key === 'confirm') {
                            onUpdateEventConfirm(next_evt);
                            return;
                        }
    
                        // revert event back to pre-move event if above logic was not executed
                        setLoading(false);
                        setEvents(events => events.map(evt => evt.restore || evt));
                    }
                });
                return;
            }

            // request confirmation to assign appointment
            let reason = null;
            utils.alert.show({
                title: 'Assign Appointment',
                message: `Are you sure that you want to assign this appointment to ${resource.full_name}? ${isRescheduling ? `This appointment will be scheduled for ${Utils.formatDate(next_evt.start, options)} to ${Utils.formatDate(next_evt.end, options)}. Please explain your reason for rescheduling below since you have chosen a new timeframe for this appointment.` : ''}`,
                ...isRescheduling && {
                    content: (
                        <div style={{
                            paddingBottom: 16,
                            paddingLeft: 16,
                            paddingRight: 16,
                            width: '100%'
                        }}>
                            <TextView
                            placeholder={'Reason for Rescheduling'}
                            onChange={text => reason = text} 
                            value={reason} />
                        </div>
                    )
                },
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: 'default'
                },{
                    key: 'abort',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {

                    // determine if a rescheduling update is needed
                    if(isRescheduling) {
                        if(key === 'confirm' && reason) {
                            onUpdateAppointmentConfirm(next_evt, reason);
                        }
                    }

                    // determine if a standard event update is needed
                    if(key === 'confirm') {
                        onUpdateAppointmentConfirm(next_evt);
                        return;
                    }

                    // revert event back to pre-move event if above logic was not executed
                    setLoading(false);
                    setEvents(events => events.map(evt => evt.restore || evt));
                }
            });
            return;
        }

        // request confirmation for reschedule if start or end date have changed
        if(isRescheduling) {
            utils.alert.show({
                title: 'Reschedule Appointment',
                message: `Are you sure that you want to reschedule this appointment for ${Utils.formatDate(next_evt.start, options)} to ${Utils.formatDate(next_evt.end, options)}? Please provide your reason for rescheduling below.`,
                textFields: [{
                    key: 'reason',
                    placeholder: 'Reason for Rescheduling',
                    type: 'textview'
                }],
                buttons: [{
                    key: 'confirm',
                    title: 'Yes',
                    style: 'default'
                },{
                    key: 'abort',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: ({ key, reason }) => {

                    // update event with new rescheduled data if applicable
                    if(reason && key === 'confirm') {
                        onUpdateAppointmentConfirm(next_evt, reason);
                        return;
                    }

                    // revert event back to pre-move event if above logic was not executed
                    setLoading(false);
                    setEvents(events => events.map(evt => evt.restore || evt));
                }
            });
            return;
        }

        // fallback to updating the event with data that does not need additional user input
        onUpdateAppointmentConfirm(next_evt);
    }

    const onUpdateAppointmentConfirm = async (evt, reason) => {
        try {

            // send request to server to update appointment
            setEventLoading(evt.abstract);
            console.log({
                duration: evt.abstract.object.duration,
                id: evt.abstract.object.id,
                primary_user_id: evt.resource_id,
                reason: reason,
                start_date: evt.start,
                timezone: evt.abstract.object.timezone,
                type: 'update_calendar_event'
            })
            let { status } = await Request.post(utils, '/appointments/', {
                duration: evt.abstract.object.duration,
                id: evt.abstract.object.id,
                primary_user_id: evt.resource_id,
                reason: reason,
                start_date: evt.start,
                timezone: evt.abstract.object.timezone,
                type: 'update_calendar_event'
            });

            // update local events state
            setLoading(false);
            setEvents(events => {
                return events.map(prev_evt => {
                    if(prev_evt.id === evt.id) {

                        // update status object for the event and the target object
                        prev_evt.status = status;
                        prev_evt.extendedProps.event.object.status = status;

                        // update the end date for the event and the target object
                        prev_evt.end = moment(evt.end).format('YYYY-MM-DD HH:mm:ss');
                        prev_evt.extendedProps.event.object.end_date = prev_evt.end;

                        // update the end date for the event and the target object
                        prev_evt.start = moment(evt.start).format('YYYY-MM-DD HH:mm:ss');
                        prev_evt.extendedProps.event.object.start_date = prev_evt.start;

                        // update the user assignment for the event and the target object
                        prev_evt.extendedProps.event.object.user_id = evt.resource_id;
                        prev_evt.resource_id = evt.resource_id;
                    }
                    return prev_evt;
                });
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating this appointment. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onUpdateEventConfirm = async evt => {
        try {

            // send request to server to update appointment
            setEventLoading(evt.abstract);
            await Request.post(utils, '/events/', {
                end_date: evt.end,
                id: evt.abstract.object.id,
                primary_user_id: evt.resource_id,
                start_date: evt.start,
                type: 'reschedule'
            });

            // update local events state
            setLoading(false);
            setEvents(events => events.map(prev_evt => {
                if(prev_evt.id === evt.id) {
                    prev_evt.end = moment(evt.end).format('YYYY-MM-DD HH:mm:ss');
                    prev_evt.extendedProps.event.object.primary_user_id = evt.resource_id;
                    prev_evt.resource_id = evt.resource_id;
                    prev_evt.start = moment(evt.start).format('YYYY-MM-DD HH:mm:ss');
                    return prev_evt;
                }
                return prev_evt;
            }));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating this event. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onUpdateTimelineEvents = () => {

        // filter out events that do not have a visible status code
        let targets = events.filter(evt => {
            return filters.includes(evt.status.code);
        }).map(evt => {

            // prepare variable for event
            let target = evt.extendedProps.event.object;

            // determine if a custom event type object should be returned
            if(evt.type === 'events') {
                return {
                    abstract: {
                        object: target,
                        type: evt.type
                    },
                    color: target.status?.color || '#9B9B9B',
                    end: moment.utc(target.end_date),
                    id: evt.id,
                    movable: true,
                    resource_id: target.primary_user_id,
                    start: moment.utc(target.start_date),
                    title: target.title
                }
            }

            // fallback to rendering event for appointment or appointment request
            return {
                abstract: {
                    object: target,
                    type: evt.type
                },
                color: target.status?.color || '#9B9B9B',
                end: moment.utc(target.end_date),
                id: evt.id,
                movable: true,
                resource_id: target.user_id,
                start: moment.utc(target.start_date),
                title: target.full_name
            }
        });

        // update local state with timeline events
        setTimelineEvents(targets);
    }

    const onUpdateUserPaging = next => {
        offset.current = next;
        setPaging({
            current_page: (offset.current / limit) + 1,
            number_of_pages: users.length > limit ? Math.ceil(users.length / limit) : 1
        });
    }

    const onUserClick = (userID, evt) => {
        let user = utils.user.get();
        utils.sheet.show({
            items: [{
                key: 'in_appointment',
                title: 'Flag as "Running a Appointment"',
                style: 'default',
                visible: isUserInAppointment(userID) === false && user.level <= User.levels.get().primary
            },{
                key: 'out_appointment',
                title: 'Flag as "Available for Appointment"',
                style: 'default',
                visible: isUserInAppointment(userID) === true && user.level <= User.levels.get().primary
            },{
                key: 'reassign',
                title: 'Reassign Tasks',
                style: 'default'
            },{
                key: 'unassign',
                title: 'Unassign Tasks',
                style: 'destructive'
            },{
                key: 'view',
                title: 'View Account Details',
                style: 'default'
            }],
            position: 'bottom',
            target: evt.target
        }, key => {
            if(['in_appointment', 'out_appointment'].includes(key) === true) {
                onSetUserAppointmentState(userID, key);
                return;
            }
            if(key === 'reassign') {
                reassignTasks(utils, userID, ['appointments'], setLoading);
                return;
            }
            if(key === 'unassign') {
                unassignTasks(utils, userID, ['appointments'], setLoading);
                return;
            }
            if(key === 'view') {
                onShowUserDetails(userID);
                return;
            }
        });
    }

    const onUsersListChange = () => {

        // filter out users who are not primarys or advisors
        let targets = users.filter(user => {
            if([User.levels.get().primary, User.levels.get().sales_ops].includes(user.level) === false) {
                return false;
            }
            return managingUsers === false && hiddenUsers.includes(user.user_id) === true ? false : true
        });

        // update paging props for visible users within the list limit
        setPaging({
            current_page: (offset.current / limit) + 1,
            number_of_pages: targets.length > limit ? Math.ceil(targets.length / limit) : 1
        });
    }

    const onUserStateChange = evt => {
        setUserStates(states => {
            try {
                return update(states, {
                    [evt.user_id]: {
                        $set: evt
                    }
                });
            } catch(e) {
                console.error(e.message);
                return states;
            }
        });
    }

    const onUserVisibilityClick = (userID, evt) => {

        // stop event from propagating to parent element
        evt.stopPropagation();

        // update local state of hidden users
        let next = hiddenUsers.includes(userID) === true ? hiddenUsers.filter(id => id !== userID) : hiddenUsers.concat([userID]);
        setHiddenUsers(next);

        // update cookie with hidden users list
        Cookies.set('appointment_calendar.hidden_user_ids', JSON.stringify(next));
    }

    const getButtons = () => {

        // determine if user managment is enabled
        if(managingUsers === true) {
            return [{
                key: 'managing_users',
                onClick: setManagingUsers.bind(this, false),
                style: 'default',
                title: 'Done'
            }];
        }

        // return list of default button options
        return [{
            key: 'day_view',
            onClick: setCalendarStyle.bind(this, 'dayGrid'),
            style: Appearance.colors.tertiary(),
            title: 'Day View'
        },{
            key: 'time_view',
            onClick: setCalendarStyle.bind(this, 'timeGrid'),
            style: Appearance.colors.primary(),
            title: 'Time View'
        },{
            key: 'week_view',
            onClick: setCalendarStyle.bind(this, 'weekGrid'),
            style: Appearance.colors.secondary(),
            title: 'Week View'
        },{
            key: 'options',
            onClick: onOptionsClick,
            style: 'grey',
            title: 'Options'
        }];
    }

    const getCalendar = () => {
        if(calendarStyle === 'dayGrid') {

            let events = getTimelineEvents();
            return (
                <div 
                className={'pr-lg-3'}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    <div style={{
                        ...Appearance.styles.unstyledPanel(),
                        width: '100%'
                    }}>
                        {events.length === 0 && (
                            Views.entry({
                                bottomBorder: false,
                                hideIcon: true,
                                subTitle: `No events were found for ${Utils.formatDate(targetDate, { exclude_time: true })}`,
                                title: 'No Events Found'
                            })
                        )}
                        {events.length > 0 && (
                            <table
                            className={'px-3 py-2 m-0'}
                            style={{
                                width: '100%'
                            }}>
                                <thead style={{
                                    width: '100%'
                                }}>
                                    <TableListHeader fields={getCalendarDayGridFields()} />
                                </thead>
                                <tbody style={{
                                    width: '100%'
                                }}>
                                    {events.sort((a,b) => {
                                        return a.start < b.start ? -1 : 1;
                                    }).map((evt, index, events) => {
                                        let fields = getCalendarDayGridFields(evt);
                                        return (
                                            <tr
                                            key={index}
                                            className={`view-entry ${window.theme}`}
                                            style={{
                                                borderBottom: index !== events.length -1 && `1px solid ${Appearance.colors.divider()}`
                                            }}>
                                                {fields.map((field, index) => {
                                                    return (
                                                        <td
                                                        key={index}
                                                        className={'px-3 py-2 flexible-table-column'}
                                                        onClick={onEventClick.bind(this, evt.abstract)}>
                                                            <span style={Appearance.textStyles.subTitle()}>{field.value}</span>
                                                        </td>
                                                    )
                                                })}
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )
        }
        if(calendarStyle === 'timeGrid') {
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minHeight: 250
                }}>
                    <Scheduler
                    date={targetDate}
                    events={getTimelineEvents()}
                    loading={loading}
                    minuteSteps={15}
                    onClick={onTimelineEventClick}
                    onDateChange={setTargetDate}
                    onRenderCallout={onRenderCallout}
                    onRenderEvent={onRenderEvent}
                    onRenderResource={onRenderResource}
                    onUpdate={onUpdateTimelineEvent}
                    operatingHours={operatingHours}
                    resources={getUsers()}
                    timezone={timezone}
                    utils={utils}
                    options={{
                        creatable: false,
                        crossResourceMove: false,
                        headerLabel: 'First and Last Name',
                        moveable: false,
                        resizable: true
                    }}
                    style={{
                        flexGrow: 1,
                        paddingRight: 15
                    }}/>
                    <PageControl
                    data={paging}
                    limit={limit}
                    offset={offset.current}
                    onClick={onUpdateUserPaging}
                    style={{
                        borderTop: 'none',
                        paddingTop: 0
                    }}/>
                </div>
            )
        }
        return (
            <div style={{
                paddingRight: 24
            }}>
                <WeekCalendar
                defaultDate={targetDate}
                draggable={true}
                events={getEvents()}
                onEventRender={onEventRender}
                onEventDrop={onEventDrop} 
                utils={utils}/>
            </div>
        )
    }

    const getCalendarDayGridFields = evt => {

        // prepare optional primary assignment user
        let assignment = evt && evt.resource_id && users.find(user => user.user_id === evt.resource_id);

        // determine if target is a custom event
        if(evt && evt.abstract.type === 'events') {

            // prepare attachment target
            let target = evt.abstract.object.appointment || evt.abstract.object.lead;

            return [{
                key: 'start',
                sortable: false,
                title: 'Scheduled Time',
                value: Utils.formatDate(moment(evt.start).utc(), { exclude_date: true, include_timezone: true, timezone })
            },{
                key: 'type',
                sortable: false,
                title: 'Event Type',
                value: evt.title
            },{
                key: 'full_name',
                sortable: false,
                title: 'Customer',
                value: target && target.full_name
            },{
                key: 'phone_number',
                sortable: false,
                title: 'Phone Number',
                value: target && target.phone_number
            },{
                key: 'address',
                sortable: false,
                title: 'Address',
                value: target && target.address && Utils.formatAddress(target.address)
            },{
                key: 'assignment',
                sortable: false,
                title: 'Assigned To',
                value: assignment && assignment.full_name || 'Not available'
            },{
                key: 'status',
                sortable: false,
                title: 'Status',
                value: evt.abstract.object.status && getEventStatus(utils, evt.abstract.object)
            }];
        }

        // fallback to rendering a appointment or appointment request entry
        return [{
            key: 'start',
            sortable: false,
            title: 'Scheduled Time',
            value: evt && Utils.formatDate(moment(evt.start).utc(), { exclude_date: true, include_timezone: true, timezone })
        },{
            key: 'type',
            sortable: false,
            title: 'Event Type',
            value: 'Appointment'
        },{
            key: 'full_name',
            sortable: false,
            title: 'Customer',
            value: evt && evt.abstract.object.full_name
        },{
            key: 'phone_number',
            sortable: false,
            title: 'Phone Number',
            value: evt && evt.abstract.object.phone_number
        },{
            key: 'address',
            sortable: false,
            title: 'Address',
            value: evt && evt.abstract.object.address && Utils.formatAddress(evt.abstract.object.address)
        },{
            key: 'assignment',
            sortable: false,
            title: 'Assigned To',
            value: assignment && assignment.full_name || 'Not available'
        },{
            key: 'status',
            sortable: false,
            title: 'Status',
            value: evt && evt.abstract.object.status && getAppointmentStatus(utils, evt.abstract.object)
        }];
    }

    const getCategories = () => {
        let shouldSelectAll = categories.length !== categoryItems.length;
        return (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Categories'}>
                <MultipleListField
                items={getCategoryItems()}
                onChange={onCategoriesChange}
                onRenderResult={(item, index, items, onRemoveItem) => {
                    let isCustomType = ['appointments'].includes(item.id) === false;
                    return (
                        <div
                        className={isCustomType ? `view-entry ${window.theme}` : ''}
                        key={index}
                        onClick={isCustomType ? onEventTypeClick.bind(this, index) : null}
                        style={{
                            alignItems: 'center',
                            borderBottom: index !== items.length - 1 ? `1px solid ${Appearance.colors.divider()}` : null,
                            display: 'flex',
                            flexDirection: 'row',
                            padding: '12px 16px 12px 16px'
                        }}>
                            <img 
                            src={item.icon.url}
                            style={{
                                borderRadius: 10,
                                height: 20,
                                marginRight: 8,
                                overflow: 'hidden',
                                width: 20
                            }} />
                            <span style={{
                                ...Appearance.textStyles.key(),
                                flexGrow: 1,
                                textAlign: 'left',
                                whiteSpace: 'normal'
                            }}>{item.title}</span>
                            <img
                            src={`images/grey-close-icon-small.png`}
                            className={'text-button'}
                            onClick={onRemoveItem.bind(this, index)}
                            style={{
                                height: 15,
                                marginLeft: 8,
                                objectFit: 'contain',
                                opacity: 1,
                                width: 15
                            }} />
                        </div>
                    )
                }}
                onAddNew={onAddNewEventType}
                placeholder={'Choose a category from the list...'}
                value={categoryItems.filter(item => categories.includes(item.id))}/>
                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    marginTop: 8,
                    width: '100%'
                }}>
                    <AltBadge
                    onClick={setCategories.bind(this, [])}
                    content={{
                        color: Appearance.colors.grey(),
                        text: 'Deselect All'
                    }}
                    outline={true}
                    style={{
                        marginRight: shouldSelectAll ? 4 : 0,
                        padding: '5px 16px 5px 16px',
                        width: '100%'
                    }}/>
                    {shouldSelectAll && (
                        <AltBadge
                        onClick={onCategoriesChange.bind(this, categoryItems)}
                        content={{
                            text: 'Select All',
                            color: Appearance.colors.primary()
                        }}
                        outline={true}
                        style={{
                            marginLeft: 4,
                            marginRight: 0,
                            padding: '5px 16px 5px 16px',
                            width: '100%'
                        }}/>
                    )}
                </div>
            </LayerItem>
        )
    }

    const getCategoryItems = () => {
        return categoryItems.filter(item => item.active !== false);
    }

    const getColor = status => {
        if(!status) {
            return null;
        }
        if(status.color) {
            return status.color;
        }
        let target = utils.client.status_codes.get().find(entry => entry.code === status.code);
        return target?.color || Appearance.colors.grey();
    }

    const getDateSelector = () => {
        if(calendarStyle === 'weekGrid') {
            return (
                <DatePickerField
                blockStyle={'week'}
                insetLabel={'The Week of'}
                highlightDates={date => {
                    return date.unix() >= moment(targetDate).startOf('week').add(1, 'days') && date.unix() <= moment(targetDate).endOf('week').add(1, 'days')
                }}
                onDateChange={setTargetDate}
                selected={targetDate}
                utils={utils} />
            )
        }
        return (
            <DatePickerDropdownField
            onChange={setTargetDate}
            removable={false}
            utils={utils}
            value={targetDate}
            style={{
                flexGrow: 1
            }} />
        )
    }

    const getDefaultCategories = () => {
        return [{
            active: true,
            icon: {url: 'images/calendar-appointments-icon.png'},
            id: 'appointments',
            title: 'Appointments'
        }];
    }

    const getEvents = userID => {
        return events.filter(calEvent => {

            // require that event status falls within the list of selected status codes
            if(filters.includes(calEvent.status.code) === false) {
                return false;
            }

            // restrict to a specific user id if applicable
            if(userID && calEvent.extendedProps.event.user_id !== userID) {
                return false;
            }

            // fallback to retricting event to event type
            let type = calEvent.type === 'events' ? calEvent.extendedProps.event.object.type.id : calEvent.extendedProps.event.type;
            return categories.length > 0 ? categories.includes(type) : true;
        });
    }

    const getEventTitle = evt => {

        // determine if event represents a appointment 
        if(evt.abstract.type === 'appointments') {

            let { first_name, last_name, spouse_first_name } = evt.abstract.object;
            return (
                <span style={{
                    ...Appearance.textStyles.title(),
                    color: 'white',
                    display: 'block',
                    overflow: 'hidden',
                    paddingLeft: 12,
                    paddingRight: 12,
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}>{`${first_name}${spouse_first_name ? ` & ${spouse_first_name}` : ''} ${last_name || ''}`}</span>
            )
        }
        
        // fallback to returning title for custom event type
        return (
            <span style={{
                ...Appearance.textStyles.title(),
                color: 'white',
                display: 'block',
                overflow: 'hidden',
                paddingLeft: 12,
                paddingRight: 12,
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}>{evt.abstract.object.title}</span>
        )
    }

    const getFilters = () => {
        return (
            <LayerItem 
            collapsed={false}
            lastItem={true}
            title={'Status Codes'}>
                <StatusCodeFilters
                categories={['appointments','events']}
                client={client}
                onChange={setFilters} 
                defaultCodes={[125,150,700,100]}
                storage={'appointments'}
                utils={utils}/>
            </LayerItem>
        )
    }

    const getLocalStorageKey = () => {
        let client = utils.client.get();
        return `${client.id}.categories.selected.calendar`;
    }

    const getRescheduledAccessoryIcon = () => {
        if(!rescheduleContext || typeof(rescheduleContext.text) !== 'string' || rescheduleContext.text.trim().length === 0) {
            return null;
        }
        return (
            <img
            className={'text-button'}
            onClick={onRescheduleContextClick.bind(this, rescheduleContext.text)}
            src={'images/event-context-icon.png'}
            style={{
                height: 25,
                marginLeft: 12,
                marginRight: 12,
                width: 25
            }} />
        )
    }

    const getRescheduleContext = calEvent => {
        let entry = rescheduleContext && rescheduleContext[`${calEvent.type}_${calEvent.object.id}`];
        return entry && entry.message ? entry : null;
    }

    const getTimelineEvents = () => {
        return timelineEvents.filter(calEvent => {
            if(filters.includes(calEvent.abstract.object.status.code) === false) {
                return false;
            }
            if(calendarStyle === 'dayGrid') {
                return moment(calEvent.start).isSame(targetDate, 'day');
            }
            return true;
        });
    }

    const getUsers = () => {
        let levels = User.levels.get();
        return users.filter(user => {

            // filter out setters as they dont attend appointments
            if(user.level === levels.setter) {
                return false;
            }

            // hidden users have to be filtered out before paging index props are applied
            return managingUsers === false && hiddenUsers.includes(user.user_id) === true ? false : true;

        }).filter((_, index) => {

            // restrict users down to current page if paging props were provided
            if(paging) {
                return index >= offset.current && index < offset.current + limit;
            }

            // return user without any additional logic 
            return true;
        });
    }

    const fetchEvents = async () => {
        try {

            // fetch calendar events from server
            setLoading(true);
            let { events, event_types, operating_hours, reschedule_context, users, user_states } = await Request.get(utils, '/events/', {
                end_date: moment(targetDate).endOf('week').add(1, 'days').format('YYYY-MM-DD'),
                reschedule_context: true,
                start_date: moment(targetDate).startOf('week').add(1, 'days').format('YYYY-MM-DD'),
                type: 'calendar',
                users_list: true
            });

            // set operating hours and reschedule context object, both are nullable
            setLoading(false);
            setOperatingHours(operating_hours);
            setRescheduleContext(reschedule_context);

            // prepare list of calendar event types
            setupEventTypes(event_types);

            // update local state with client users and user states
            setUserStates(user_states);
            setUsers(prev => {

                // loop through existing users and remove subscriptions before new users list is fetched
                prev.forEach(async user => {
                    try {
                        console.log(`removing listener for ${user.user_id}`)
                        await utils.sockets.off('users', `on_state_change_${user.user_id}`, onUserStateChange);
                    } catch(e) {
                        console.error(e.message);
                    }
                });

                // loop through new users and subscribe to state changes
                users.forEach(async user => {
                    try {
                        console.log(`adding listener for ${user.user_id}`)
                        await utils.sockets.persistOn('users', `on_state_change_${user.user_id}`, onUserStateChange);
                    } catch(e) {
                        console.error(e.message);
                    }
                });
                
                return users;
            });

            // loop through events and format for calendar
            setEvents(events.map(target => {
                return {
                    end: moment.utc(target.object.end_date),
                    eventResourceEditable: true,
                    eventStartEditable: true,
                    extendedProps: {
                        event: target
                    },
                    id: target.object.id,
                    start: moment.utc(target.object.start_date),
                    status: target.object.status,
                    type: target.type
                }
            }));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the appointments calendar. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const fetchHiddenUsers = () => {
        let hiddenUsers = Cookies.get('appointment_calendar.hidden_user_ids') || [];
        if(typeof(hiddenUsers) === 'string') {
            hiddenUsers = JSON.parse(hiddenUsers);
        }
        setHiddenUsers(hiddenUsers);
    }

    const formatAbstractEvent = abstract => {

        // appointments and custom events share a similar structure but no additional formatting is required for custom events
        if(abstract.type === 'events') {
            return { 
                object: {
                    ...abstract.object,
                    primary_user_id: abstract.object.primary_user ? abstract.object.primary_user.user_id : abstract.object.primary_user_id
                },
                type: abstract.type
            };
        }

        return {
            object: {
                created: abstract.object.created,
                date: abstract.object.date,
                duration: abstract.object.duration,
                end_date: abstract.object.end_date,
                first_name:abstract.object.lead.first_name,
                full_name: abstract.object.lead.full_name,
                last_name: abstract.object.lead.last_name,
                lead_type: abstract.object.lead.lead_type,
                id: abstract.object.id,
                phone_number: abstract.object.lead.phone_number,
                spouse_first_name: abstract.object.lead.spouse_first_name,
                start_date: abstract.object.start_date,
                status: abstract.object.status,
                street_address_1: abstract.object.address && abstract.object.address.street_address_1,
                timezone: abstract.object.timezone,
                user_id: abstract.object.primary ? abstract.object.primary.user_id : abstract.object.primary_user_id
            },
            type: abstract.type
        }
    }

    const isUserInAppointment = userID => {
        return userStates && userStates[userID] && userStates[userID].state === 'in_appointment' ? true : false;
    }

    const setupEventTypes = async types => {
        try {
            
            // format custom categories using response event types
            let eventTypes = types.map(type => Event.Type.create(type)).filter(type => type.active);
    
            // sort categories alphabetically and update state
            let selections = getDefaultCategories().concat(eventTypes).sort((a,b) => a.title.localeCompare(b.title));
            setCategoryItems(selections);

            // fetch cached selection for filters from local storage
            let key = getLocalStorageKey();
            let state = localStorage.getItem(key);

            // filter out non-selection ids if applicable and update values state
            if(state) {

                // parse cached selections and prepare new list of selected codes
                let ids = JSON.parse(state);
                selections = selections.filter(entry => ids.includes(entry.id));
            }

            // set state for selections
            setCategories(selections.map(entry => entry.id));

        } catch(e) {
            console.error(e.message);
        }
    }

    useEffect(() => {
        onUpdateTimelineEvents();
    }, [calendarStyle, events, timezone]);

    useEffect(() => {
        fetchEvents();
    }, [targetDate]);

    useEffect(() => {
        onUsersListChange();
    }, [managingUsers, users]);

    useEffect(() => {

        fetchHiddenUsers();
        utils.events.on(panelID, 'client_change', onClientChange);
        utils.events.on(panelID, 'client_preferences_update', fetchEvents);
        utils.events.on(panelID, 'slot_change', fetchEvents);
        
        utils.content.subscribe(panelID, ['appointments', 'appointment_status', 'events', 'event_types'], {
            onFetch: fetchEvents,
            onUpdate: onContentUpdate
        });
        return () => {

            // remove content subscriptions and event subscriptions
            utils.content.unsubscribe(panelID);
            utils.events.off(panelID, 'client_change', onClientChange);
            utils.events.off(panelID, 'client_preferences_update', fetchEvents);
            utils.events.off(panelID, 'slot_change', fetchEvents);

            // loop through users and remove websocket subscriptions
            setUsers(users => {
                users.forEach(async user => {
                    try {
                        console.log(`removing listener for ${user.user_id}`)
                        await utils.sockets.off('users', `on_state_change_${user.user_id}`, onUserStateChange);
                    } catch(e) {
                        console.error(e.message);
                    }
                });
                return users;
            });
        }
    }, [])

    return (
        <Panel
        index={index}
        utils={utils}
        title={`Calendar: ${timezone?.replace('_', ' ') || ''}`}
        options={{
            ...options,
            buttons: getButtons(),
            loading: loading === true
        }}>
            <PermissionsContainer
            permission={calendarStyle === 'timeGrid' ? 'calendar.time_view' : 'calendar.week_view'} 
            utils={utils}>
                <div className={'row p-0 m-0'}>
                    <div
                    ref={contentContainer}
                    className={'col-9 p-0'}
                    style={{
                        height: '100%'
                    }}>
                        {getCalendar()}
                    </div>
                    <div
                    className={'col-3 p-0'}
                    style={{
                        maxHeight: 940,
                        overflowY: 'scroll',
                        position: 'relative'
                    }}>
                        <div 
                        className={'p-3 mb-3'}
                        style={{
                            ...Appearance.styles.unstyledPanel(),
                            width: '100%'
                        }}>
                            <LayerItem
                            collapsed={false}
                            lastItem={true}
                            title={'Date'}>
                                {getDateSelector()}
                            </LayerItem>
                        </div>
                        <div 
                        className={'p-3 mb-3'}
                        style={{
                            ...Appearance.styles.unstyledPanel(),
                            width: '100%'
                        }}>
                            <LayerItem
                            collapsed={false}
                            lastItem={true}
                            title={'Timezone'}>
                                <TimezoneLookupField
                                onChange={setTimezone}
                                utils={utils} 
                                value={timezone} />
                            </LayerItem>
                        </div>
                        <div 
                        className={'p-3 mb-3'}
                        style={{
                            ...Appearance.styles.unstyledPanel(),
                            width: '100%'
                        }}>
                            {getCategories()}
                        </div>
                        <div 
                        className={'p-3'}
                        style={{
                            ...Appearance.styles.unstyledPanel(),
                            width: '100%'
                        }}>
                            {getFilters()}
                        </div>
                    </div>
                </div>
            </PermissionsContainer>
        </Panel>
    )
}

export const AppointmentsList = ({ cache_key }, { index, options, utils }) => {

    const limit = 15;
    const offset = useRef(0);

    const sorting = useRef({ 
        sort_key: 'start_date',
        sort_type: Content.sorting.type.descending
    });

    const [appointments, setAppointments] = useState([]);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paging, setPaging] = useState(null);
    const [searchText, setSearchText] = useState(null);
    const [showExtendedFields, setShowExtendedFields] = useState(false);
    const [updateAvailable, setUpdateAvailable] = useState(false);

    const onAppointmentClick = async id => {
        try {

            // start loading and fetch appointment details
            setLoading(id);
            let appointment = await Appointment.get(utils, id);

            // end loading and show appointment details layer
            setLoading(false);
            utils.layer.open({
                abstract: Abstract.create({
                    object: appointment,
                    type: 'appointments'
                }),
                Component: AppointmentDetails,
                id: `appointment_details_${appointment.id}`,
                permissions: ['appointments.details']
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the details for this appointment. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onAppointmentListDataChange = evt => {
        try {

            // no additional logic is needed if the update is not meant for this panel
            let { initiated_by_user_id, key } = evt;
            if(!cache_key || key !== `list.${cache_key}`) {
                return;
            }

            // automatically update panel contents if the change was made by the current user
            // otherwise show a button on the panel that allows the user to manually refresh the contents of the panel
            if(initiated_by_user_id === utils.user.get().user_id) {
                console.log(`[appointments.list.${cache_key}]: automatically fetching for user who initiated data change`);
                fetchAppointments();
            } else {
                console.log(`[appointments.list.${cache_key}]: update available`);
                setUpdateAvailable(true);
            }

        } catch(e) {
            console.error(`[on_appointment_list_data_change]: ${e.message}`);
        }
    }

    const onPrintAppointments = async props => {
        return new Promise(async (resolve, reject) => {
            try {
                setLoading(true);
                let { appointments } = await Request.get(utils, '/appointments/', {
                    search_text: searchText,
                    type: 'all',
                    ...sorting.current,
                    ...props
                });

                setLoading(false);
                resolve(appointments);

            } catch(e) {
                setLoading(false);
                reject(e);
            }
        });
    }

    const onToggleExtendedFields = () => {
        setShowExtendedFields(status => !status);
    }

    const getContent = () => {
        if(appointments.length === 0) {
            return (
                Views.entry({
                    bottomBorder: false,
                    hideIcon: true,
                    subTitle: `No appointments were found in the system`,
                    title: `No Appointments Found`
                })
            )
        }
        return (
            <table
            className={'px-3 py-2 m-0'}
            style={{
                width: '100%'
            }}>
                <thead style={{
                    width: '100%'
                }}>
                    {getFields()}
                </thead>
                <tbody style={{
                    width: '100%'
                }}>
                    {appointments.map((appointment, index) => {
                        return getFields(appointment, index)
                    })}
                </tbody>
            </table>
        )
    }

    const getFields = (appointment, index) => {

        let target = appointment || {};
        let fields = [{
            key: 'full_name',
            title: 'Full Name',
            value: target.full_name
        },{
            color: target.meeting_id ? null : Appearance.colors.orange,
            key: 'address',
            title: 'Meeting Status',
            value: target.meeting_id ? 'Scheduled' : 'Not Scheduled',
        },{
            key: 'start_date',
            title: 'Scheduled Date',
            value: target.start_date && Utils.formatDate(target.start_date, { include_timezone: true, timezone: target.timezone })
        },{
            key: 'created',
            title: 'Created',
            value: target.created && Utils.formatDate(target.created)
        },{
            key: 'user',
            title: 'Closer',
            value: target.user ? target.user.full_name : null,
            visible: showExtendedFields
        },{
            key: 'status',
            title: 'Status',
            sortable: false,
            value: getAppointmentStatus(utils, target)
        }];

        // create table headers with custom sorting options
        // conform external sort to match internal header sort if applicable
        if(!appointment) {
            return (
                <TableListHeader
                fields={fields}
                onChange={props => {
                    sorting.current = props;
                    fetchAppointments();
                }}
                value={sorting.current} />
            )
        }

        // loop through result rows and prepare table rows
        return (
            <tr
            key={index}
            className={`view-entry ${window.theme}`}
            style={{
                borderBottom: index !== appointments.length - 1 && `1px solid ${Appearance.colors.divider()}`
            }}>
                {fields.map((field, index) => {
                    if(field.visible === false) {
                        return null
                    }
                    return (
                        <td
                        key={index}
                        className={'px-3 py-2 flexible-table-column'}
                        onClick={onAppointmentClick.bind(this, appointment.id)}>
                            <span style={{
                                ...Appearance.textStyles.subTitle(),
                                ...field.color && { color: field.color }
                            }}>{field.value}</span>
                        </td>
                    )
                })}
            </tr>
        )
    }

    const getPrintProps = () => {
        return {
            headers: [{
                key: 'full_name',
                title: 'Full Name'
            },{
                key: 'address',
                title: 'Address'
            },{
                key: 'start_date',
                title: 'Date'
            },{
                key: 'user',
                title: 'Assigned To'
            },{
                key: 'status',
                title: 'Status'
            }],
            onFetch: onPrintAppointments,
            onRenderItem: item => ({
                address: item.address ? Utils.formatAddress(item.address) : null,
                full_name: item.full_name,
                start_date: item.start_date ? Utils.formatDate(item.start_date, { include_timezone: true, timezone: item.timezone }) : null,
                status: getAppointmentStatus(utils, item),
                user: item.user ? item.user.full_name : null
            }),
            permissions: ['appointments.actions.print']
        }
    }

    const getSubTitleProps = () => {
        if(updateAvailable) {
            return {
                onClick: fetchAppointments,
                style: {
                    ...Appearance.textStyles.subTitle(),
                    color: Appearance.colors.green,
                    fontWeight: 700
                },
                text: 'New Content Available'
            }
        }
        return {
            text: lastUpdated ? `Last Updated: ${Utils.formatDate(lastUpdated)}` : 'Awaiting content...'
        }
    }

    const connectToSockets = async () => {
        try {
            await utils.sockets.persistOn('appointments', 'on_appointment_list_data_change', onAppointmentListDataChange);
        } catch(e) {
            console.error(e.message);
        }
    }

    const disconnectFromSockets = async () => {
        try {
            await utils.sockets.off('appointments', 'on_appointment_list_data_change', onAppointmentListDataChange);
        } catch(e) {
            console.error(e.message);
        }
    }

    const fetchAppointments = async () => {
        try {
            setLoading(true);
            let { appointments, last_updated, paging } = await Request.get(utils, '/appointments/', {
                cache_key: 'general',
                limit: limit,
                offset: offset.current,
                search_text: searchText,
                type: 'all',
                ...sorting.current
            });

            setLoading(false);
            setLastUpdated(last_updated && moment.utc(last_updated).local());
            setPaging(paging);
            setAppointments(appointments.map(appointment => ({
                ...appointment,
                created: appointment.created && moment.utc(appointment.created).local(),
                start_date: appointment.start_date && moment.utc(appointment.start_date)
            })));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the appointments list. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        fetchAppointments();
    }, [searchText]);

    useEffect(() => {

        connectToSockets();

        // subscribe to client event changes
        utils.events.on('appointments', 'client_change', fetchAppointments);
        utils.events.on('appointments', 'client_preferences_update', fetchAppointments);
        utils.events.on('appointments', 'slot_change', fetchAppointments);

        // subscribe to abstract target changes
        utils.content.subscribe('appointments', ['appointments', 'appointment_status'], {
            onFetch: fetchAppointments,
            onUpdate: abstract => {
                setAppointments(appointments => {
                    return appointments.map(appointment => {
                        if(appointment.id !== abstract.getID()) {
                            return appointment;
                        }
                        if(abstract.type === 'appointment_status') {
                            return {
                                ...appointment,
                                status: abstract.object.status
                            }
                        }
                        return {
                            ...abstract.object,
                            start_date: abstract.object.start_date && moment(abstract.object.start_date).utc()
                        };
                    })
                });
            }
        });

        return () => {
            disconnectFromSockets();
            utils.content.unsubscribe('appointments');
            utils.events.off('appointments', 'client_change');
            utils.events.off('appointments', 'client_preferences_update', fetchAppointments);
            utils.events.off('appointments', 'slot_change', fetchAppointments);
        }
    }, []);

    return (
        <Panel
        index={index}
        title={'Appointments'}
        utils={utils}
        options={{
            ...options,
            buttons: [{
                key: 'toggle_extended',
                onClick: onToggleExtendedFields,
                style: showExtendedFields ? 'default' : 'secondary',
                title: `${showExtendedFields ? 'Hide' : 'Show'} Assignment`
            }],
            loading: loading,
            onRefreshContent: {
                pulse: {
                    color: Appearance.colors.green,
                    enabled: updateAvailable
                },
                onClick: fetchAppointments
            },
            print: getPrintProps(),
            paging: {
                data: paging,
                limit: limit,
                offset: offset,
                onClick: next => {
                    offset.current = next;
                    fetchAppointments();
                }
            },
            search: {
                onChange: setSearchText,
                placeholder: 'Search by appointment id or lead first and last name...'
            },
            subTitle: getSubTitleProps()
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel()
            }}>
                {getContent()}
            </div>
        </Panel>
    )
}

export const CustomerFeedbackResponses = ({ index, options, utils }) => {

    const panelID = 'customer_feedback_responses';
    const limit = 15;

    const [loading, setLoading] = useState(null);
    const [offset, setOffset] = useState(0);
    const [paging, setPaging] = useState(null);
    const [responses, setResponses] = useState([]);
    const [searchText, setSearchText] = useState(null);
    const [sorting, setSorting] = useState(null);

    const getFields = (response, index) => {

        let target = response || {};
        let fields = [{
            key: 'customer',
            title: 'Customer',
            value: target.full_name
        },{
            key: 'appointment_date',
            title: 'Appointment Date',
            value: target.start_date ? moment(target.start_date).format('MMM Do, YYYY [at] h:mma') : null
        },{
            key: 'feedback_date',
            title: 'Feedback Date',
            value: target.date ? moment(target.date).format('MMM Do, YYYY [at] h:mma') : null
        }];

        // create table headers with custom sorting options
        // conform external sort to match internal header sort if applicable
        if(!response) {
            let sorts = {
                [Content.sorting.type.alphabetically]: 'customer',
                [Content.sorting.type.ascending]: 'feedback_date',
                [Content.sorting.type.descending]: 'feedback_date'
            };
            return (
                <TableListHeader
                fields={fields}
                onChange={props => setSorting(props)}
                {...sorting && sorting.general === true && {
                    value: {
                        key: sorts[sorting.sort_type],
                        direction: sorting.sort_type
                    }
                }} />
            )
        }

        // loop through result rows
        return (
            <tr
            key={index}
            className={`view-entry ${window.theme}`}
            style={{
                borderBottom: `${index !== responses.length - 1 ? 1 : 0}px solid ${Appearance.colors.divider()}`
            }}>
            {fields.map((field, index) => {
                return (
                    <td
                    key={index}
                    className={'px-3 py-2 flexible-table-column'}
                    onClick={onResponseClick.bind(this, response)}>
                        <span style={Appearance.textStyles.subTitle()}>{field.value}</span>
                    </td>
                )
            })}
            </tr>
        )
    }

    const getPrintProps = () => {
        return {
            onFetch: onPrintResponses,
            onRenderItem: (item, index, items) => {
                return {
                    customer: item.full_name,
                    appointment_date: item.start_date ? moment(item.start_date).format('MMM Do, YYYY [at] h:mma') : null,
                    feedback_date: item.date ? moment(item.date).format('MMM Do, YYYY [at] h:mma') : null
                }
            },
            headers: [{
                key: 'customer',
                title: 'Customer'
            },{
                key: 'appointment_date',
                title: 'Appointment Date'
            },{
                key: 'feedback_date',
                title: 'Feedback Date'
            }]
        }
    }

    const onPrintResponses = async props => {
        return new Promise(async (resolve, reject) => {
            try {
                setLoading(true);
                let { responses } = await Request.get(utils, '/clients/', {
                    type: searchText ? 'lookup_feedback_responses' : 'feedback_responses',
                    search_text: searchText,
                    ...sorting,
                    ...props
                });

                setLoading(false);
                resolve(responses.map(response => Feedback.Response.create(response)));

            } catch(e) {
                setLoading(false);
                reject(e);
            }
        })
    }

    const fetchResponses = async () => {
        try {
            setLoading(true);
            let { paging, responses } = await Request.get(utils, '/clients/', {
                type: 'feedback_responses',
                limit: limit,
                offset: offset,
                search_text: searchText,
                ...sorting
            });

            setLoading(false);
            setPaging(paging);
            setResponses(responses.map(response => Feedback.Response.create(response)))

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the responses list. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onResponseClick = response => {
        utils.sheet.show({
            items: [{
                key: 'appointments',
                title: 'View Appointment',
                style: 'default'
            },{
                key: 'lead',
                title: 'View Lead',
                style: 'default'
            },{
                key: 'response',
                title: 'View Customer Response',
                style: 'default'
            }]
        }, async key => {
            if(key === 'appointments') {
                try {
                    let appointment = await Appointment.get(utils, response.appointment_id);
                    utils.layer.open({
                        abstract: Abstract.create({
                            object: appointment,
                            type: 'appointments',
                        }),
                        id: `appointment_details_${appointment.id}`,
                        Component: AppointmentDetails,
                        permissions: ['appointments.details']
                    })
                } catch(e) {
                    utils.alert.show({
                        title: 'Oops!',
                        message: `There was an issue retrieving the information for this appointment. ${e.message || 'An unknown error occurred'}`
                    })
                }
                return;
            }

            if(key === 'lead') {
                try {
                    let appointment = await Appointment.get(utils, response.appointment_id);
                    let lead = await Lead.get(utils, appointment.lead.id);
                    utils.layer.open({
                        abstract: Abstract.create({
                            object: lead,
                            type: 'leads'
                        }),
                        Component: LeadDetails,
                        id: `lead_details_${lead.id}`,
                        permissions: ['leads.details']
                    })
                } catch(e) {
                    utils.alert.show({
                        title: 'Oops!',
                        message: `There was an issue retrieving the information for this appointment. ${e.message || 'An unknown error occurred'}`
                    })
                }
                return;
            }

            if(key === 'response') {
                utils.layer.open({
                    id: `feedback_response_details_${response.id}`,
                    abstract: Abstract.create({
                        type: 'feedback_response',
                        object: response
                    }),
                    Component: FeedbackResponseDetails
                });
                return;
            }
        })
    }

    const getContent = () => {
        if(responses.length === 0) {
            return (
                Views.entry({
                    bottomBorder: false,
                    hideIcon: true,
                    subTitle: 'No customer responses were found in the system',
                    title: 'No Customer Responses Found'
                })
            )
        }
        return (
            <table
            className={'px-3 py-2 m-0'}
            style={{
                width: '100%'
            }}>
                <thead style={{
                    width: '100%'
                }}>
                    {getFields()}
                </thead>
                <tbody style={{
                    width: '100%'
                }}>
                    {responses.map((response, index) => {
                        return getFields(response, index);
                    })}
                </tbody>
            </table>
        )
    }

    useEffect(() => {
        fetchResponses();
    }, [offset, searchText, sorting]);

    useEffect(() => {
        utils.events.on(panelID, 'client_change', fetchResponses);
        utils.events.on(panelID, 'client_preferences_update', fetchResponses);
        utils.content.subscribe(panelID, ['feedback_responses'], {
            onFetch: fetchResponses,
            onUpdate: abstract => {
                setResponses(responses => {
                    return responses.map(response => {
                        return response.id === abstract.getID() ? abstract.object : response
                    })
                })
            }
        });
        return () => {
            utils.content.unsubscribe(panelID);
            utils.events.off(panelID, 'client_change', fetchResponses);
            utils.events.off(panelID, 'client_preferences_update', fetchResponses);
        }
    }, []);

    return (
        <Panel
        index={index}
        title={'Customer Responses'}
        utils={utils}
        options={{
            ...options,
            loading: loading,
            search: {
                placeholder: 'Search by first or last name...',
                onChange: setSearchText
            },
            print: getPrintProps(),
            paging: {
                data: paging,
                limit: limit,
                offset: offset,
                onClick: nextOffset => setOffset(nextOffset)
            }
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel()
            }}>
                {getContent()}
            </div>
        </Panel>
    )
}

export const FeedbackTemplates = ({ index, options, utils }) => {

    const panelID = 'feedback_templates';
    const limit = 15;

    const [loading, setLoading] = useState(null);
    const [offset, setOffset] = useState(0);
    const [paging, setPaging] = useState(null);
    const [searchText, setSearchText] = useState(null);
    const [sorting, setSorting] = useState(null);
    const [templates, setTemplates] = useState([]);

    const getActiveStatus = template => {
        if(!template) {
            return;
        }
        let color = template.active ? Appearance.colors.primary() : Appearance.colors.grey();
        return (
            <div
            className={'text-button'}
            onClick={onChangeActiveStatus.bind(this, template)}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: 100,
                height: '100%',
                maxWidth: 75,
                textAlign: 'center',
                border: `2px solid ${color}`,
                background: Appearance.colors.softGradient(color),
                borderRadius: 5,
                overflow: 'hidden'
            }}>
                <span style={{
                    ...Appearance.textStyles.subTitle(),
                    color: 'white',
                    fontWeight: '600',
                    width: '100%'
                }}>{template.active ? 'Active' : 'Not Active'}</span>
            </div>
        )
    }

    const onChangeActiveStatus = async (template, e) => {
        try {
            e.stopPropagation();
            if(!template.client_id) {
                utils.alert.show({
                    title: 'Just a Second',
                    message: 'This is a standard template meant for all Clients and can not be edited'
                });
                return;
            }
            await Request.post(utils, '/clients/', {
                type: 'set_feedback_template_active_status',
                id: template.id,
                active: !template.active
            });
            setTemplates(templates => {
                return templates.map(prevTemplate => {
                    if(prevTemplate.id === template.id) {
                        prevTemplate.active = !prevTemplate.active;
                    }
                    return prevTemplate;
                })
            })

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue changing the status of this template. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onNewTemplate = () => {

        let template = Feedback.Template.new();
        template.client = utils.client.get();
        template.client_id = template.client.id;

        utils.layer.open({
            id: 'new_feedback_template',
            abstract: Abstract.create({
                type: 'feedback_template',
                object: template
            }),
            Component: AddEditFeedbackTemplate.bind(this, {
                isNewTarget: true
            })
        })
    }

    const onPrintTemplates = async props => {
        return new Promise(async (resolve, reject) => {
            try {
                setLoading(true);
                let { templates } = await Request.get(utils, '/clients/', {
                    type: searchText ? 'lookup_feedback_templates' : 'feedback_templates',
                    search_text: searchText,
                    ...sorting,
                    ...props
                });

                setLoading(false);
                resolve(templates.map(template => Feedback.Template.create(template)));

            } catch(e) {
                setLoading(false);
                reject(e);
            }
        })
    }

    const onTemplateClick = template => {
        utils.layer.open({
            id: `feedback_template_details_${template.id}`,
            abstract: Abstract.create({
                type: 'feedback_template',
                object: template
            }),
            Component: FeedbackTemplateDetails
        })
    }

    const getContent = () => {
        if(templates.length === 0) {
            return (
                Views.entry({
                    title: 'No Templates Found',
                    subTitle: 'No templates were found in the system',
                    bottomBorder: false,
                    hideIcon: true
                })
            )
        }
        return (
            <table
            className={'px-3 py-2 m-0'}
            style={{
                width: '100%'
            }}>
                <thead style={{
                    width: '100%'
                }}>
                    {getFields()}
                </thead>
                <tbody style={{
                    width: '100%'
                }}>
                    {templates.map((template, index) => {
                        return getFields(template, index);
                    })}
                </tbody>
            </table>
        )
    }

    const getFields = (template, index) => {

        let target = template || {};
        let fields = [{
            key: 'title',
            title: 'Title',
            value: target.title
        },{
            key: 'description',
            title: 'Description',
            value: target.description
        },{
            key: 'is_default',
            title: 'Default for Client',
            value: target.is_default ? 'Yes' : 'No'
        },{
            key: 'status',
            title: 'Status',
            value: getActiveStatus(target)
        }];

        // create table headers with custom sorting options
        // conform external sort to match internal header sort if applicable
        if(!template) {
            let sorts = {
                [Content.sorting.type.alphabetically]: 'title',
                [Content.sorting.type.ascending]: 'date',
                [Content.sorting.type.descending]: 'date'
            };
            return (
                <TableListHeader
                fields={fields}
                onChange={props => setSorting(props)}
                {...sorting && sorting.general === true && {
                    value: {
                        key: sorts[sorting.sort_type],
                        direction: sorting.sort_type
                    }
                }} />
            )
        }

        // loop through result rows
        return (
            <tr
            key={index}
            className={`view-entry ${window.theme}`}
            style={{
                borderBottom: `${index !== templates.length - 1 ? 1 : 0}px solid ${Appearance.colors.divider()}`
            }}>
            {fields.map((field, index) => {
                return (
                    <td
                    key={index}
                    className={'px-3 py-2 flexible-table-column'}
                    onClick={onTemplateClick.bind(this, template)}>
                        <span style={Appearance.textStyles.subTitle()}>{field.value}</span>
                    </td>
                )
            })}
            </tr>
        )
    }

    const getPrintProps = () => {
        return {
            onFetch: onPrintTemplates,
            onRenderItem: (item, index, items) => {
                return {
                    title: item.title,
                    description: item.description,
                    is_default: item.is_default ? 'Yes' : 'No',
                    status: getActiveStatus(item)
                }
            },
            headers: [{
                key: 'title',
                title: 'Title'
            },{
                key: 'description',
                title: 'Description'
            },{
                key: 'is_default',
                title: 'Default for Client'
            },{
                key: 'status',
                title: 'Status'
            }]
        }
    }

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            let { templates, paging } = await Request.get(utils, '/clients/', {
                type: 'feedback_templates',
                limit: limit,
                offset: offset,
                search_text: searchText,
                ...sorting
            });

            setLoading(false);
            setPaging(paging);
            setTemplates(templates.map(template => Feedback.Template.create(template)))

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the templates list. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    useEffect(() => {
        fetchTemplates();
    }, [offset, searchText, sorting]);

    useEffect(() => {
        utils.events.on(panelID, 'client_change', fetchTemplates);
        utils.events.on(panelID, 'client_preferences_update', fetchTemplates);
        utils.content.subscribe(panelID, ['feedback_templates'], {
            onFetch: fetchTemplates,
            onUpdate: abstract => {
                setTemplates(templates => {
                    return templates.map(template => {
                        return template.id === abstract.getID() ? abstract.object : template
                    })
                })
            }
        });
        return () => {
            utils.content.unsubscribe(panelID);
            utils.events.off(panelID, 'client_change', fetchTemplates);
            utils.events.off(panelID, 'client_preferences_update', fetchTemplates);
        }
    }, []);

    return (
        <Panel
        index={index}
        title={'Templates'}
        utils={utils}
        options={{
            ...options,
            buttons: [{
                key: 'new',
                title: 'New Template',
                style: 'default',
                onClick: onNewTemplate
            }],
            loading: loading,
            print: getPrintProps(),
            paging: {
                data: paging,
                limit: limit,
                offset: offset,
                onClick: setOffset
            },
            search: {
                placeholder: 'Search by template title...',
                onChange: setSearchText
            }
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel()
            }}>
                {getContent()}
            </div>
        </Panel>
    )
}

// Layers
export const AddEditAppointment = ({ abstract, index, options, utils }) => {

    const layerID = `edit_appointment_${abstract.getID()}`;
    const [duration, setDuration] = useState(3600);
    const [layerState, setLayerState]= useState(null);
    const [loading, setLoading] = useState(false);
    const [reason, setReason] = useState(null);
    const [startDate, setStartDate] = useState(abstract.object.start_date);
    const [timezone, setTimezone] = useState(abstract.object.timezone);

    const onReschedule = async () => {
        try {

            // verify that a start date and duration were provided
            if(!startDate || !duration) {
                throw new Error('Please choose a start date and duration before moving on');
            }

            // verify that a reason for rescheduling was provided
            if(!reason) {
                throw new Error('Please provide a reason for rescheduling this appointment');
            }

            // set loading flag and sleep briefly 
            setLoading('done');
            await Utils.sleep(0.25);

            // submit request to server
            let { status } = await Request.post(utils, '/appointments/', {
                duration: duration,
                id: abstract.getID(),
                reason: reason,
                start_date: moment.tz(startDate, timezone).utc().format('YYYY-MM-DD HH:mm:ss'),
                timezone: timezone,
                type: 'reschedule'
            });

            // end loading and update target with new values
            setLoading(false);
            abstract.object.duration = duration;
            abstract.object.end_date = moment.tz(startDate, timezone).utc().add(duration, 'seconds');
            abstract.object.status = status;
            abstract.object.start_date = moment.tz(startDate, timezone).utc();
            abstract.object.timezone = timezone;

            // show success message and update content
            utils.banner.show({
                title: 'Appointment Rescheduled',
                message: `This appointment has been rescheduled for ${Utils.formatDate(abstract.object.start_date, { include_timezone: true, timezone })}`,
                onShow: () => {
                    setLayerState('close');
                    utils.content.update(abstract);
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating this appointment. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const getButtons = () => {
        return [{
            color: startDate && duration ? 'primary' : 'dark',
            key: 'done',
            loading: loading === 'done',
            onClick: onReschedule,
            text: 'Save Changes'
        }];
    }

    const getDurationSelector = () => {

        // duration is the total length of the timeslot itself
        let durations = [ 3600, 5400, 7200, 9000, 10800, 12600, 14400, 16200, 18000 ];
        return (
            <LayerItem
            collapsed={false}
            title={'Duration'}>
                <select
                className={`custom-select ${window.theme}`}
                onChange={e => {
                    let id = Utils.attributeForKey.select(e, 'id');
                    setDuration(parseInt(id));
                }}
                style={{
                    background: `${Appearance.colors.textField()} url("images/custom-select-list-arrows-${window.theme === 'dark' ? 'white' : 'black'}.png") no-repeat right 0.75rem center`,
                    backgroundSize: '10px',
                    width: '100%'
                }}
                value={Utils.parseDuration(duration)}>
                    {durations.map((duration, index) => {
                        return (
                            <option key={index} id={duration}>{Utils.parseDuration(duration)}</option>
                        )
                    })}
                </select>
            </LayerItem>
        )
    }

    const setupEditing = () => {

        // calculate number of seconds for duration between start and end date
        let start = moment(abstract.object.start_date);
        let end = moment(abstract.object.end_date);
        let duration = end.diff(start, 'seconds');

        // nornalize duration to closest 30 minute increment
        if(duration % 1800 !== 0) {
            duration = Math.round(duration / 1800) * 1800;
        }
        setDuration(duration);
    }

    useEffect(() => {
        setupEditing();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={`Reschedule Appointment`}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            <LayerItem 
            collapsed={false}
            title={'Date and Time'}>
                <DateDurationPickerField
                onChange={result => {
                    setStartDate(result.date);
                    setTimezone(result.timezone);
                }}
                placeholder={'Choose a start date...'}
                utils={utils}
                value={{
                    date: startDate,
                    timezone: timezone
                }}
                style={{
                    width: '100%'
                }}/>
            </LayerItem>
            {getDurationSelector()}
            <LayerItem
            collapsed={false}
            lastItem={true}
            title={'Reason for Rescheduling'}>
                <TextView
                utils={utils}
                onChange={text => setReason(text)}
                style={{
                    width: '100%'
                }}/>
            </LayerItem>
        </Layer>
    )
}

export const AddEditFeedbackTemplate = ({ isNewTarget }, { abstract, index, options, utils }) => {

    const layerID = isNewTarget ? `new_feedback_template` : `edit_feedback_template_${abstract.getID()}`;
    const [loading, setLoading] = useState(false);
    const [layerState, setLayerState] = useState(null);
    const [template, setTemplate] = useState(null);

    const onDoneClick = async () => {

        // Valdiate fields
        let items = getFields().reduce((array, field) => {
            return array.concat(field.items);
        }, []);
        let required = items.find(item => {
            if(item.required === false) {
                return false;
            }
            return item.value === null || item.value === undefined;
        });
        if(required) {
            utils.alert.show({
                title: 'Just a Second',
                message: `Please fill out the "${required.title}" before moving on`
            });
            return;
        }

        // Submit survey
        if(isNewTarget) {
            try {
                setLoading('done');
                await Utils.sleep(0.25);
                await abstract.object.submit(utils);

                setLoading(false);
                utils.banner.show({
                    title: 'Template Created',
                    message: `Your new template has been created`,
                    onShow: () => setLayerState('close')
                });

            } catch(e) {
                setLoading(false);
                utils.alert.show({
                    title: 'Oops!',
                    message: `There was an issue creating this template. ${e.message || 'An unknown error occurred'}`
                })
            }
            return;
        }

        // Update survey
        try {
            setLoading('done');
            await Utils.sleep(0.25);
            await abstract.object.update(utils);

            setLoading(false);
            utils.banner.show({
                title: 'Template Updated',
                message: `This template has been updated`,
                onShow: () => setLayerState('close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating this template. ${e.message || 'An unknown error occurred'}`
            })
        }
        return;
    }

    const onEditClick = item => {

        if(typeof(item.prompt) === 'function') {
            item.prompt();
            return;
        }

        let props = {
            title: item.title,
            description: item.description
        };

        let tmpValue = item.value;
        switch(item.component) {

            case 'textview':
            props.children = (
                <TextView
                value={tmpValue}
                useDelay={false}
                onChange={text => {
                    if(item.nesting) {
                        tmpValue = {
                            ...template[item.nesting],
                            [item.key]: text
                        };
                        return;
                    }
                    tmpValue = text;
                }}
                containerStyle={{
                    width: '100%'
                }}/>
            )
            break;

            case 'image_picker':
            tmpValue = item.nesting ? template[item.nesting] : item.selected;
            props.children = (
                <ImagePickerField
                utils={utils}
                value={item.selected}
                onChange={image => {
                    if(item.nesting) {
                        tmpValue = {
                            ...template[item.nesting],
                            [item.key]: image
                        };
                        return;
                    }
                    tmpValue = image;
                }} />
            );
            break;

            case 'pattern_picker':
            tmpValue = item.selected;
            props.children = (
                <PatternPickerField
                utils={utils}
                collapsible={false}
                selected={item.selected}
                onChange={pattern => {
                    tmpValue = {
                        ...template.props,
                        pattern: pattern
                    };
                }}/>
            )
            break;

            case 'question_editor':
            tmpValue = item.selected;
            props.children = (
                <QuestionEditor
                utils={utils}
                value={item.selected}
                onClose={() => utils.layer.requestClose('edit_target')}
                onUpdate={question => tmpValue = question}
                onAdd={question => {
                    question.order_index = template.questions.length + 1;
                    tmpValue = question;
                }} />
            )
            break;

            case 'bool_toggle':
            props.children = (
                <BoolToggle
                enabled={'Yes'}
                disabled={'No'}
                isEnabled={item.enabled}
                onChange={enabled => tmpValue = enabled}
                containerStyle={{
                    width: '100%'
                }}/>
            )
            break;
        }

        utils.layer.open({
            id: 'edit_target',
            Component: EditTarget.bind(this, {
                ...props,
                ...item.layerProps,
                onCommit: () => onValueChange(item.nesting || item.key, tmpValue)
            })
        })
    }

    const onImageClick = itemKey => {
        utils.sheet.show({
            items: [{
                key: 'change',
                title: 'Change',
                style: 'default'
            },{
                key: 'remove',
                title: 'Remove',
                style: 'destructive'
            }]
        }, key => {
            if(key === 'change') {
                let item = getCustomizations()[0].items.find(prevItem => prevItem.key === itemKey);
                if(!item) {
                    return;
                }
                item.prompt = false;
                onEditClick(item);
                return;
            }
            if(key === 'remove') {
                utils.alert.show({
                    title: 'Remove Image',
                    message: 'Are you sure that you want to remove this image?',
                    buttons: [{
                        key: 'confirm',
                        title: 'Remove',
                        style: 'destructive'
                    },{
                        key: 'cancel',
                        title: 'Cancel',
                        style: 'default'
                    }],
                    onClick: async key => {
                        if(key !== 'confirm') {
                            return;
                        }
                        onUpdateTarget({
                            props: {
                                ...template.props,
                                header_image: null
                            }
                        })
                    }
                })
                return;
            }
        })
    }

    const onSortChange = ({  newIndex, oldIndex }) => {
        let question = template.questions[oldIndex];
        let edits = abstract.object.set({
            questions: update(template.questions, {
                $splice: [[ oldIndex, 1 ], [ newIndex, 0, question ]]
            }).map((question, index) => {
                question.order_index = index + 1;
                return question;
            })
        });
        setTemplate({ ...edits });
    }

    const onUpdateTarget = props => {
        let edits = abstract.object.set(props);
        setTemplate(edits);
    }

    const onValueChange = async (key, value) => {
        if([ 'new_question', 'questions' ].includes(key)) {
            let edits = abstract.object.set({
                questions: update(abstract.object.edits.questions, {
                    $apply: questions => {
                        let nextQuestions = questions.filter(prevQuestion => {
                            return prevQuestion.id !== value.id;
                        });
                        nextQuestions.push(value);
                        return nextQuestions;
                    }
                })
            })
            setTemplate(edits);
            return;
        }

        let edits = abstract.object.set({ [key]: value })
        setTemplate(edits);
    }

    const getCustomizations = () => {

        if(!template) {
            return null;
        }
        return [{
            key: 'customize',
            title: 'Customize',
            items: [{
                key: 'header_image',
                nesting: 'props',
                required: false,
                title: 'Hero Image',
                description: 'Would you like to add an image at the top of your template? We will show your client name, city, and state if no image is added.',
                value: template.props.header_image ? 'Image Added' : null,
                selected: template.props.header_image,
                component: 'image_picker',
                prompt: template.props.header_image ? onImageClick.bind(this, 'header_image') : null
            },{
                key: 'welcome_message',
                nesting: 'props',
                required: false,
                title: 'Welcome Message',
                description: 'Would you like your welcome message to say? This message is shown at the top of your template.',
                value: template.props.welcome_message,
                component: 'textview',
            },{
                key: 'pattern',
                nesting: 'props',
                required: false,
                title: 'Background Pattern',
                description: 'Would you like to add a pattern to the background of this template?',
                selected: template.props ? template.props.pattern : null,
                value: template.props ? convertPattern(template.props.pattern) : null,
                component: 'pattern_picker',
                layerProps: { sizing: 'medium' }
            }]
        }]
    }

    const getFields = () => {

        if(!template) {
            return [];
        }
        let items = [{
            key: 'details',
            title: 'Details',
            items: [{
                key: 'title',
                title: 'Title',
                description: 'What is the title of this Template? This title will appear when a customer visits a feedback form that uses this template',
                value: template.title,
                required: true,
                component: 'textfield',
                onChange: text => onUpdateTarget({ title: text })
            },{
                key: 'description',
                title: 'Description',
                description: 'What is the description for this Template? This description will not be shown to a customer when they visit a feedback form that uses this template',
                value: template.description,
                required: true,
                component: 'textview',
                onChange: text => onUpdateTarget({ description: text })
            },{
                key: 'client',
                title: 'Client',
                description: 'What client do you want to assign to this template?',
                value: template.client,
                required: utils.user.get().level <= User.levels.get().admin,
                visible: utils.user.get().level <= User.levels.get().admin,
                component: 'client_lookup',
                onChange: text => onUpdateTarget({ client: text })
            }]
        }]
        return items;
    }

    const getLockedQuestions = () => {
        return getQuestions().filter(question => {
            return question.locked;
        })
    }

    const getQuestions = () => {
        if(!template || !template.questions) {
            return [];
        }
        return template.questions.sort((a,b) => {
            return a.order_index > b.order_index;
        }).map(question => {

            let value = null;
            switch(question.type) {
                case Feedback.Template.Question.type.list:
                value = 'Dropdown Menu';
                break;

                case Feedback.Template.Question.type.checkboxes:
                value = 'Multiple Checkboxes';
                break;

                case Feedback.Template.Question.type.picker:
                value = 'List of Options';
                break;

                case Feedback.Template.Question.type.textfield:
                value = 'Short Text Response';
                break;

                case Feedback.Template.Question.type.textview:
                value = 'Long Text Response';
                break;

                default:
                value = 'Unknown Question Type';
            }

            return {
                key: 'questions',
                id: question.id,
                title: question.title || 'New Question',
                description: 'What would you like to ask for this question? Choose a question type from the list below to get started.',
                value: value,
                selected: question,
                locked: question.client_id ? false : true,
                component: 'question_editor',
                layerProps: { sizing: 'medium' }
            }
        });
    }

    const getQuestionComponent = (question, index, locked) => {
        let { title, required, value } = question;
        return (
            <div
            key={index}
            className={locked ? '' : `view-entry ${window.theme}`}
            onClick={locked ? null : onEditClick.bind(this, question)}
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '8px 12px 8px 12px',
                width: '100%',
                borderBottom: `1px solid ${Appearance.colors.divider()}`
            }}>
                {locked && (
                    <img
                    src={'images/lock-icon-small.png'}
                    style={{
                        width: 15,
                        height: 15,
                        objectFit: 'contain',
                        marginRight: 8
                    }} />
                )}
                <span style={{
                    ...Appearance.textStyles.key(),
                    flexGrow: 1,
                    paddingRight: 20
                }}>{title}</span>
                <span style={{
                    ...Appearance.textStyles.value()
                }}>{value || 'Not added'}</span>
                <img
                src={'images/next-arrow-grey-small.png'}
                style={{
                    width: 12,
                    height: 12,
                    objectFit: 'contain',
                    marginLeft: 8,
                    opacity: 0.75
                }} />
            </div>
        )
    }

    const getSortableQuestions = () => {
        return getQuestions().filter(question => {
            return question.locked ? false : true;
        })
    }

    const QuestionsList = props => {
        if(!template) {
            return null;
        }
        const Container = SortableContainer(({ questions }) => {
            if(!questions) {
                return null;
            }
            return (
                <ul style={{
                    width: '100%',
                    borderRadius: 8,
                    overflow: 'hidden',
                    margin: 0,
                    marginBlock: 0,
                    padding: 0
                }}>
                    {questions.map((question, index) => (
                        <SortableQuestion
                        index={index}
                        key={`question_${question.id}`}
                        question={question} />
                    ))}
                </ul>
            );
        });
        return (
            <div style={{
                width: '100%'
            }}>
                <Container
                {...props}
                helperClass={'sortable-container'}
                pressDelay={200} />

                <div style={{
                    padding: 12,
                    textAlign: 'center'
                }}>
                    <span
                    className={'text-button'}
                    onClick={onEditClick.bind(this, {
                        key: 'new_question',
                        title: 'Add a New Question',
                        description: 'What would you like to ask for this question? Choose a question type from the list below to get started.',
                        value: 'Click to add a question...',
                        component: 'question_editor',
                        required: !template.questions || template.questions.length === 0,
                        layerProps: { sizing: 'medium' }
                    })}
                    style={{
                        ...Appearance.textStyles.title(),
                        color: Appearance.colors.primary()
                    }}>{'Add a New Question'}</span>
                </div>
            </div>
        );
    };

    const SortableQuestion = SortableElement(({ index, question }) => {
        if(!template) {
            return null;
        }
        return (
            <li style={{
                width: '100%'
            }}>
                {getQuestionComponent(question, index, false)}
            </li>
        )
    });

    const setupTarget = async () => {
        try {

            let edits = abstract.object.open();
            if(isNewTarget) {
                let { questions } = await Request.get(utils, '/clients/', {
                    type: 'default_feedback_template_questions'
                });
                edits = abstract.object.set({
                    questions: questions.map(question => {
                        return Feedback.Template.Question.create(question);
                    })
                })
            }
            setTemplate(edits);

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue setting up this template. ${e.message || 'An unknown error occurred'}`,
                onClick: () => setLayerState('close')
            })
        }
    }

    useEffect(() => {
        setupTarget();
    }, []);

    return (
        <Layer
        id={layerID}
        title={isNewTarget ? `New Feedback Template` : `Editing ${abstract.getTitle()}`}
        index={index}
        utils={utils}
        options={{
            ...options,
            loading: loading === true,
            layerState: layerState
        }}
        buttons={[{
            key: 'done',
            text: isNewTarget ? 'Done' : 'Save Changes',
            color: 'primary',
            loading: loading === 'done',
            onClick: onDoneClick
        }]}>
            <AltFieldMapper
            utils={utils}
            fields={getFields()} />

            <LayerItem
            title={'Questions'}
            collapsed={false}>
                <div style={{
                    ...Appearance.styles.unstyledPanel()
                }}>
                    {getLockedQuestions().map((question, index) => {
                        return getQuestionComponent(question, index, true);
                    })}
                    <QuestionsList
                    questions={getSortableQuestions()}
                    onSortEnd={onSortChange} />
                </div>
            </LayerItem>

            <FieldMapper
            editable={true}
            sections={getCustomizations()}
            onEditClick={onEditClick}
            utils={utils} />
        </Layer>
    )
}

export const BookAppointmentFromLead = ({ onBookAppointment, onCloseLayer }, { abstract, index, options, utils }) => {

    const layerID = `book_appointment_${abstract.getID()}`;
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState(null);

    const onBookAppointmentConfirmation = ({ lead_status, start_date, timezone }) => {

        // update status for target and notify subscribers
        abstract.object.status = lead_status;
        utils.content.update(abstract);

        // notify subscribers that new appointment data is available
        utils.content.fetch('appointments');

        // show confirmation banner
        utils.banner.show({
            title: 'Appointment Booked',
            message: `The appointment for ${abstract.object.full_name} has been ${user ? ` assigned to ${user.full_name}` : 'set'} for ${Utils.formatDate(start_date, { include_timezone: true, timezone })}`,
            onShow: () => {
                setLayerState('close');
                utils.content.fetch('appointments');
                if(typeof(onBookAppointment) === 'function') {
                    onBookAppointment();
                }
            }
        });
    }

    const getPresetUsers = () => {
        return Lead.assignments.guess(abstract.object);
    }

    return (
        <Layer
        id={layerID}
        title={`Set Appointment for ${abstract.object.full_name}`}
        index={index}
        utils={utils}
        options={{
            ...options,
            loading: loading,
            sizing: 'medium',
            layerState: layerState,
            onCloseLayer: onCloseLayer
        }}>
            <BookAppointment
            lead={abstract.object}
            onBookAppointment={onBookAppointmentConfirmation}
            onLoad={loading => setLoading(loading)}
            onUserChange={user => setUser(user)}
            presetUsers={getPresetUsers()} 
            utils={utils}/>
        </Layer>
    )
}

export const AppointmentDetails = ({ abstract, index, options, utils }) => {

    const layerID = `appointment_details_${abstract.getID()}`;
    const eventsLimit = 5;

    const [appointment, setAppointment] = useState(abstract.object);
    const [bookedBy, setBookedBy] = useState(null);
    const [events, setEvents] = useState([]);
    const [eventsOffset, setEventsOffset] = useState(0);
    const [eventsPaging, setEventsPaging] = useState(null);
    const [feedbackTemplate, setFeedbackTemplate] = useState(null);
    const [layerState, setLayerState] = useState(null);
    const [lead, setLead] = useState(null);
    const [loading, setLoading] = useState('init');
    const [primary, setPrimary] = useState(null);

    const onCancelAppointment = () => {
        utils.alert.show({
            title: 'Cancel Appointment',
            message: `Are you sure that you want to cancel this appointment?`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Do Not Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onCancelAppointmentConfirm();
                    return;
                }
            }
        });
    }

    const onCancelAppointmentConfirm = async () => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);
            let { status } = await Request.post(utils, '/appointments/', {
                id: abstract.getID(),
                type: 'cancel_appointment'
            });

            // update target with new status and notify subscribers
            abstract.object.status = status;
            setAppointment(abstract.object);
            utils.content.update(abstract);

            setLoading(false);
            utils.banner.show({
                title: 'Appointment Cancelled',
                message: 'This appointment has been cancelled',
                onShow: () => setLayerState('close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue cancelling this appointment. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onCreateMeeting = () => {

        // prevent moving forward if no email address has been provided for the lead associated with this appointment
        if(!lead.email_address) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'A meeting can not be created for this appointment because the associated lead does not have an email address on file. Please add an email address for the lead before creating a meeting for this appointment.',
                buttons: [{
                    key: 'edit_lead',
                    title: 'Edit Lead',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'edit_lead') {
                        onEditLeadClick();
                        return;
                    }
                }
            });
            return;
        }
        
        // request confirmation from user to create meeting
        utils.alert.show({
            title: 'Create Meeting',
            message: 'Are you sure that you want to create a meeting for this appointment? This will send out meeting invites to the assigned user and the lead associated with this appointment.',
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onCreateMeetingConfirm();
                    return;
                }
            }
        });
    }

    const onCreateMeetingConfirm = async () => {
        try {

            // set loading flag and submit request to server
            setLoading('options');
            await Utils.sleep(0.25);
            let { id } = await Request.post(utils, '/appointments/', {
                id: abstract.getID(),
                type: 'create_meeting'
            });

            // update target with meeting id and notify subscribers
            abstract.object.meeting_id = id;
            setAppointment(abstract.object);
            utils.content.update(abstract);

            // end loading and show confirmation banner
            setLoading(false);
            utils.banner.show({
                title: 'Meeting Created',
                message: 'A meeting has been created for this appointment and invites have been sent out to the lead and associated users.'
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue creating a meeting for this appointment. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onDeleteAppointment = () => {
        utils.alert.show({
            title: 'Delete Appointment',
            message: `Are you sure that you want to delete this appointment? This can not be undone.`,
            buttons: [{
                key: 'confirm',
                title: 'Yes',
                style: 'destructive'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'default'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onDeleteAppointmentConfirm();
                    return;
                }
            }
        });
    }

    const onDeleteAppointmentConfirm = async () => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);
            await Request.post(utils, '/appointments/', {
                id: abstract.getID(),
                type: 'delete_appointment'
            });

            setLoading(false);
            utils.banner.show({
                title: 'Appointment Deleted',
                message: 'This appointment has been deleted from your appointments list.',
                onShow: () => { 
                    setLayerState('close');
                    setTimeout(utils.content.fetch.bind(this, 'appointments'), 500);
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue deleting this appointment. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onEditClick = () => {
        utils.layer.open({
            abstract: abstract,
            Component: AddEditAppointment,
            id: `edit_appointment_${abstract.getID()}`
        })
    }

    const onEditLeadClick = () => {
        utils.layer.open({
            abstract: Abstract.create({
                type: 'leads',
                object: lead
            }),
            Component: AddEditLead.bind(this, {
                isNewTarget: false,
                onUpdateLead: result => {
                    abstract.object.lead = result;
                    setLead(result);
                }
            }),
            id: `edit_lead_${lead.id}`,
            permissions: ['leads.details']
        });
    }

    const onJoinMeeting = () => {
        utils.layer.open({
            abstract: abstract,
            Component: JitsiMeeting.bind(this, { token: null }),
            id: `jitsi_meeting_${abstract.object.meeting_id}`
        });
    }

    const onLeadClick = () => {
        utils.layer.open({
            abstract: Abstract.create({
                object: lead,
                type: 'leads'
            }),
            Component: LeadDetails,
            id: `lead_details_${lead.id}`,
            permissions: ['leads.details']
        });
    }

    const onNewSystemEvent = data => {
        try {
            setEvents(events => {
                return update(events, {
                    $unshift: [SystemEvent.create(data)]
                });
            });
        } catch(e) {
            console.error(e.message);
        }
    }

    const onOptionsClick = evt => {
        utils.sheet.show({
            items: [{
                key: 'cancel_appointment',
                permissions: ['appointments.actions.cancel'],
                title: 'Cancel Appointment',
                style: 'destructive',
                visible: appointment.status?.code !== Appointment.status.get().cancelled
            },{
                key: 'create_meeting',
                permissions: ['appointments.actions.create_meeting'],
                title: 'Create Meeting',
                style: 'default',
                visible: appointment.meeting_id ? false : true
            },{
                key: 'assignment',
                permissions: ['appointments.actions.edit'],
                title: `${appointment.primary_user ? 'Change' : 'Assign'} Closer`,
                style: 'default'
            },{
                key: 'delete_appointment',
                permissions: ['appointments.actions.delete'],
                title: 'Delete Appointment',
                style: 'destructive'
            },{
                key: 'resend_meeting_invite',
                permissions: ['appointments.actions.resend_meeting_invite'],
                title: 'Resend Meeting Invite',
                style: 'default',
                visible: appointment.meeting_id ? true : false
            },{
                key: 'join_meeting',
                permissions: ['appointments.actions.join_meeting'],
                title: 'Join Meeting',
                style: 'default',
                visible: appointment.meeting_id ? true : false
            },{
                key: 'appointment_status',
                permissions: ['appointments.actions.status'],
                title: 'Set Appointment Status',
                style: 'default'
            },{
                key: 'lead_status',
                permissions: ['leads.actions.status'],
                title: 'Set Lead Status',
                style: 'default'
            }].sort((a,b) => {
                return a.title.localeCompare(b.title)
            }),
            target: evt.target
        }, key => {

            if(key === 'assignment') {

                // prevent appointment rescheduling for user accounts not found in the array
                if(![
                    User.levels.get().admin,
                    User.levels.get().primary,
                    User.levels.get().setter
                ].includes(utils.user.get().level)) {
                    utils.alert.show({
                        title: 'Just a Second',
                        message: 'It looks like your account is unable to create or update appointment assignments. Please speak with your primary or setter if you need to make changes to the assignment for this appointment.'
                    });
                    return;
                }

                // present options to choose new assignment user
                utils.layer.open({
                    abstract: abstract,
                    Component: SetAppointmentAssignment,
                    id: `set_appointment_assignment_${abstract.getID()}`
                });
                return;
            }
            if(key === 'appointment_status') {
                utils.layer.open({
                    id: `set_appointment_status_${abstract.getID()}`,
                    abstract: abstract,
                    Component: SetAppointmentStatus
                });
                return;
            }
            if(key === 'cancel_appointment') {
                onCancelAppointment();
                return;
            }
            if(key === 'create_meeting') {
                onCreateMeeting();
                return;
            }
            if(key === 'delete_appointment') {
                onDeleteAppointment();
                return;
            }
            if(key === 'join_meeting') {
                onJoinMeeting();
                return;
            }
            if(key === 'lead_status') {
                onSetLeadStatus();
                return;
            }
            if(key === 'resend_meeting_invite') {
                onResendMeetingInvite();
                return;
            }
        });
    }

    const onRequestMeetingUrl = async () => {
        try {

            let { url } = await Request.get(utils, '/appointments/', {
                category: 'host',
                id: abstract.getID(),
                type: 'generate_meeting_url'
            });

            // show confirmation alert
            utils.alert.show({
                title: 'All Done!',
                message: 'Your meeting link has been prepared. Just click the button below to copy the link to your clipboard.',
                buttons: [{
                    key: 'copy',
                    title: 'Copy Link',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key === 'copy') {

                        // request a copy to clipboard of the zoom link for this appointment
                        Utils.copyText(url);

                        // show confirmation banner
                        utils.banner.show({
                            title: `Meeting Link Copied`,
                            message: `The host link for this appointment has been copied to your clipboard.`
                        }); 
                    }
                }
            });                    
        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue preparing the meeting link. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onResendMeetingInvite = () => {
        let emailAddress = lead.email_address;
        utils.alert.show({
            title: 'Resend Meeting Invite',
            message: 'Are you sure that you want to resend the meeting invite for this appointment?',
            content: (
                <div style={{
                    paddingBottom: 16,
                    paddingLeft: 16,
                    paddingRight: 16,
                    width: '100%'
                }}>
                    <TextField
                    fieldStyle={{
                        textAlign: 'center'
                    }}
                    placeholder={'Email Address'}
                    onChange={text => emailAddress = text}
                    value={emailAddress} />
                </div>
            ),
            buttons: [{
                key: 'confirm',
                title: 'Resend',
                style: 'primary'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm' && emailAddress) {
                    onResendMeetingInviteConfirm(emailAddress);
                    return;
                }
            }
        });
    }

    const onResendMeetingInviteConfirm = async emailAddress => {
        try {
            setLoading('options');
            await Utils.sleep(0.25);
            await Request.post(utils, '/appointments/', {
                email_address: emailAddress,
                id: abstract.getID(),
                type: 'resend_meeting_invite'
            });

            setLoading(false);
            utils.banner.show({
                title: 'Meeting Invite Resent',
                message: `The meeting invite for this appointment has been resent to ${emailAddress}.`,
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue resending the meeting invite for this appointment. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onSetLeadStatus = () => {
        utils.layer.open({
            abstract: Abstract.create({
                type: 'leads',
                object: lead
            }),
            id: `set_lead_status_${abstract.getID()}`,
            Component: SetLeadStatus
        });
    }

    const onUserClick = async userID => {
        try {
            setLoading(true);
            let user = await User.get(utils, userID);

            setLoading(false);
            utils.layer.open({
                abstract: Abstract.create({
                    object: user,
                    type: 'users'
                }),
                Component: UserDetails,
                id: `user_details_${user.user_id}`,
                permissions: ['users.details']
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the account information for this user. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const getButtons = () => {
        return [{
            color: 'secondary',
            key: 'options',
            loading: loading === 'options',
            onClick: onOptionsClick,
            text: 'Options'
        },{
            color: 'primary',
            key: 'edit',
            onClick: onEditClick,
            permissions: ['appointments.actions.edit'],
            text: 'Reschedule'
        }];
    }

    const getFields = () => {

        // prevent moving forward if initial loading is in progress
        if(loading === 'init' || !appointment) {
            return null;
        }

        // prepare appointment detail items
        let items = [{
            key: 'details',
            permissions: ['appointments.details.general'],
            title: 'About this Appointment',
            items: [{
                key: 'created',
                title: 'Booking Date',
                value: appointment.created ? Utils.formatDate(appointment.created) : null
            },{
                key: 'start_date',
                title: 'Date and Time (Customer Timezone)',
                value: appointment.start_date && Utils.formatDate(appointment.start_date, { include_timezone: true, timezone: appointment.timezone })
            },{
                key: 'start_date',
                title: 'Date and Time (My Timezone)',
                value: appointment.start_date && Utils.formatDate(appointment.start_date, { include_timezone: true, timezone: utils.user.get().timezone })
            },{
                key: 'duration',
                title: 'Duration',
                value: appointment.duration && Utils.parseDuration(appointment.duration)
            },{
                key: 'feedback_template',
                title: 'Feedback Template',
                value: feedbackTemplate ? feedbackTemplate.title : 'Disabled',
                style: {
                    value: {
                        color: feedbackTemplate ? Appearance.colors.subText() : Appearance.colors.red
                    }
                }
            },{
                key: 'id',
                title: 'ID',
                value: appointment.id
            },{
                color: appointment.status ? appointment.status.color : null,
                key: 'status',
                title: 'Status',
                value: appointment.status ? appointment.status.text : null
            },{
                key: 'timezone',
                title: 'Timezone',
                value: appointment.timezone
            }]
        },{
            key: 'users',
            lastItem: false,
            permissions: ['appointments.details.users'],
            title: 'Assignments and Credit',
            items: [{
                key: 'booked_by',
                onClick: bookedBy ? onUserClick.bind(this, bookedBy.user_id) : null,
                title: 'Booked By',
                value: bookedBy ? bookedBy.full_name : null
            },{
                key: 'user',
                onClick: primary ? onUserClick.bind(this, primary.user_id) : null,
                title: 'Closer',
                value: primary ? primary.full_name : null
            }]
        },{
            key: 'meeting',
            lastItem: false,
            permissions: ['appointments.details.users'],
            title: 'Meeting Details',
            items: [{
                key: 'meeting_id',
                onClick: appointment.meeting_id ? null : onCreateMeeting,
                title: 'Meeting ID',
                value: appointment.meeting_id || 'Click to Schedule'
            },{
                color: appointment.meeting_id ? null : Appearance.colors.orange,
                key: 'meeting_status',
                title: 'Status',
                value: appointment.meeting_id ? 'Scheduled' : 'Not Scheduled'
            },{
                key: 'customer_meeting_url',
                title: 'Customer Meeting Link',
                value: `https://meet.life-wipe.com/${appointment.meeting_id}`,
                visible: appointment.meeting_id ? true : false
            },{
                key: 'host_meeting_url',
                onClick: onRequestMeetingUrl,
                title: 'Host Meeting Link',
                value: 'Click to copy',
                visible: appointment.meeting_id ? true : false
            }]
        }];

        // prevent moving forward if no valid items were provided
        let count = formatFields(utils, items).length;
        if(count === 0) {
            return null;
        }

        return (
            <FieldMapper
            sections={items}
            utils={utils}/>
        )
    }

    const getLeadFields = () => {

        // prevent moving forward if feature is disabled for current user
        if(!appointment || utils.user.permissions.get('appointments.details.lead') === false) {
            return null;
        }

        // prepare items for lead fields
        let items = [{
            key: 'location',
            lastItem: false,
            title: 'Location',
            visible: appointment.lead.address && appointment.lead.location ? true : false,
            items: [{
                component: 'map',
                key: 'location',
                title: 'Location',
                value: appointment.lead.location
            },{
                key: 'address',
                title: 'Address',
                value: Utils.formatAddress(appointment.lead.address)
            },{
                key: 'maps',
                onClick: () => {
                    let address = Utils.formatAddress(appointment.lead.address);
                    window.open(`https://www.google.com/maps/place/${encodeURIComponent(address)}`)
                },
                title: 'Directions',
                value: 'Click to View'
            }]
        }];

        return (
            <FieldMapper
            sections={items}
            utils={utils} />
        )
    }

    const getLead = () => {

        // prevent moving forward if feature is disabled for current user
        if(utils.user.permissions.get('appointments.details.lead') === false) {
            return null;
        }

        // render lead component if a lead is available
        return lead && (
            <LayerItem 
            collapsed={false}
            title={'Lead'}>
                <div style={Appearance.styles.unstyledPanel()}>
                    {Views.entry({
                        bottomBorder: false,
                        icon: {
                            path: lead.lead_type?.icon.url,
                            imageStyle: {
                                backgroundColor: null
                            }
                        },
                        onClick: onLeadClick,
                        subTitle: lead.phone_number || lead.email_address || 'No contact information provided',
                        title: lead.full_name || 'Customer name not available',
                    })}
                </div>
            </LayerItem>
        )
    }

    const getSystemEvents = () => {
        return loading === false && (
            <SystemEventsLayerItem 
            abstract={abstract} 
            permissions={['appointments.details.system_events']}
            utils={utils} />
        )
    }

    const connectToSockets = async () => {
        try {
            await utils.sockets.on('system', 'on_new_event', onNewSystemEvent);
            await utils.sockets.emit('system', 'join_events', { tag: abstract.getTag()} );
        } catch(e) {
            console.error(e.message);
        }
    }

    const disconnectFromSockets = async () => {
        try {
            await utils.sockets.off('system', 'on_new_event', onNewSystemEvent);
        } catch(e) {
            console.error(e.message);
        }
    }

    const fetchDetails = async () => {
        try {

            // set loading flag if flag is not set to init
            if(loading !== 'init') {
                setLoading(true);
            }

            // fetch appointment details from the server
            let {  booked_by_user, feedback_template, lead, primary_user } = await Request.get(utils, '/appointments/', {
                id: abstract.getID(),
                type: 'ext_details'
            });

            // set lead details for appointment
            abstract.object.lead = Lead.create(lead);
            setLead(abstract.object.lead);

            // set booked_by user and update state
            abstract.object.booked_by_user = booked_by_user && User.create(booked_by_user);
            setBookedBy(abstract.object.booked_by_user);

            // set primary user and update state
            abstract.object.primary_user = primary_user && User.create(primary_user);
            setPrimary(abstract.object.primary_user);

            // set feedback template and update state
            abstract.object.feedback_template = feedback_template && Feedback.Template.create(feedback_template);
            setFeedbackTemplate(abstract.object.feedback_template);
            setLoading(false);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the additional information for this appointment. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const fetchEvents = async () => {
        try {
            let { events, paging } = await Request.get(utils, '/utils/', {
                limit: eventsLimit,
                offset: eventsOffset,
                target_id: abstract.getID(),
                target_type: abstract.type,
                type: 'system_events'
            });

            if(loading === 'system_events') {
                setLoading(false);
            }
            setEventsPaging(paging);
            setEvents(events.map(evt => SystemEvent.create(evt)));

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue loading the system events list. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    useEffect(() => {
        fetchEvents();
    }, [eventsOffset]);

    useEffect(() => {

        connectToSockets();
        setTimeout(fetchDetails, 250);

        utils.content.subscribe(layerID, ['appointments', 'leads', 'outbound_calls', 'users'], {
            onUpdate: next => {
                if(next.getTag() === abstract.getTag()) {
                    fetchDetails();
                    fetchEvents();
                }
            }
        })

        return () => {
            disconnectFromSockets();
            utils.content.unsubscribe(layerID);
        }
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={`${abstract.getTitle()} Details`}
        utils={utils}
        options={{
            ...options,
            extension: {
                abstract: abstract,
                permissions: {
                    delete: ['appointments.notes.actions.delete'],
                    edit: ['appointments.notes.actions.edit'],
                    new: ['appointments.notes.actions.new'],
                    view: ['appointments.notes']
                },
                type: 'notes'
            },
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            {getLead()}
            {getLeadFields()}
            {getFields()}
            {getSystemEvents()}
        </Layer>
    )
}

export const FeedbackResponseDetails = ({ abstract, index, options, utils }) => {

    const layerID = `feedback_response_details_${abstract.getID()}`;
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [appointment, setAppointment] = useState(null);
    const [responses, setResponses] = useState(null);

    const getAnswers = () => {
        if(!responses) {
            return null;
        }
        return (
            <LayerItem
            title={'Questionnaire'}
            style={{
                overflow: 'visible'
            }}>
                {responses.map((item, index) => {
                    return (
                        <div
                        key={index}
                        style={{
                            ...Appearance.styles.unstyledPanel(),
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            padding: '8px 12px 8px 12px',
                            marginBottom: index !== responses.length - 1 ? 8 : 0
                        }}>
                            <span style={{
                                ...Appearance.textStyles.title()
                            }}>{item.title}</span>
                            <span style={{
                                ...Appearance.textStyles.subTitle(),
                                whiteSpace: 'normal'
                            }}>{item.answer}</span>
                        </div>
                    )
                })}
            </LayerItem>
        )
    }

    const getFields = () => {
        let items = [{
            key: 'details',
            title: 'About this Response',
            items: [{
                key: 'id',
                title: 'ID',
                value: abstract.getID()
            },{
                key: 'full_name',
                title: 'Customer',
                value: abstract.object.full_name
            },{
                key: 'start_date',
                title: 'Appointment Date',
                value: abstract.object.start_date ? moment(abstract.object.start_date).format('MMMM Do, YYYY [at] h:mma') : null
            },{
                key: 'date',
                title: 'Feedback Date',
                value: abstract.object.date ? moment(abstract.object.date).format('MMMM Do, YYYY [at] h:mma') : null
            }]
        }]
        return items;
    }

    const getAppointment = () => {
        if(!appointment) {
            return null;
        }
        return (
            <div style={{
                marginBottom: 20
            }}>
                <span style={{
                    ...Appearance.textStyles.subHeader(),
                    display: 'block',
                    marginBottom: 8
                }}>{'Appointment'}</span>
                <div style={Appearance.styles.unstyledPanel()}>
                    {Views.entry({
                        title: appointment.lead.full_name,
                        subTitle: appointment.lead.phone_number || appointment.lead.email_address || 'No contact information provided',
                        hideIcon: true,
                        bottomBorder: false,
                        bottomBorder: false,
                        onClick: () => {
                            utils.layer.open({
                                abstract: Abstract.create({
                                    object: appointment,
                                    type: 'appointments',
                                }),
                                Component: AppointmentDetails,
                                id: `appointment_details_${appointment.id}`,
                                permissions: ['appointments.details']
                            })
                        }
                    })}
                </div>
            </div>
        )
    }

    const fetchDetails = async () => {
        try {
            let { appointment, responses } = await Request.get(utils, '/clients/', {
                type: 'feedback_response_details',
                id: abstract.getID()
            });
            setAppointment(Appointment.create(appointment));
            setResponses(responses);

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue retrieving the information for this customer response. ${e.message || 'An unknown error occurred'}`,
                onClick: () => setLayerState('close')
            })
        }
    }

    useEffect(() => {
        fetchDetails();
    }, []);

    return (
        <Layer
        id={layerID}
        title={`${abstract.getTitle()} Details`}
        index={index}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            sizing: 'medium'
        }}>
            {getAppointment()}
            <FieldMapper 
            sections={getFields()} 
            utils={utils} />
            {getAnswers()}
        </Layer>
    )
}

export const FeedbackTemplateDetails = ({ abstract, index, options, utils }) => {

    const layerID = `feedback_template_details_${abstract.getID()}`;
    const [loading, setLoading] = useState(false);
    const [url, setURL] = useState(null);
    const [layerState, setLayerState] = useState(null);

    const onChangeActiveStatus = () => {
        utils.alert.show({
            title: `Change to ${abstract.object.active ? 'Inactive' : 'Active'}`,
            message: `Are you sure that you want to set "${abstract.object.title}" as ${abstract.object.active ? 'inactive' : 'active'}?`,
            buttons: [{
                key: 'confirm',
                title: `Set as ${abstract.object.active ? 'Inactive' : 'Active'}`,
                style: abstract.object.active ? 'destructive' : 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: abstract.object.active ? 'default' : 'destructive'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onChangeActiveStatusConfirm();
                }
            }
        });
    }

    const onChangeActiveStatusConfirm = async () => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);

            await Request.post(utils, '/clients/', {
                type: 'set_feedback_template_active_status',
                active: !abstract.object.active,
                id: abstract.getID()
            });

            abstract.object.active = !abstract.object.active;
            utils.content.update(abstract);

            setLoading(false);
            utils.banner.show({
                title: 'Status Changed',
                message: `This template has been set as ${abstract.object.active ? 'active' : 'inactive'}`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the active status for this template. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onCopyURL = async url => {

        let textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.top = 0;
        textArea.style.left = 0;
        textArea.style.position = 'fixed';
        textArea.style.opacity = 0;
        document.body.appendChild(textArea);

        textArea.focus();
        textArea.select();

        try {
            let copied = document.execCommand('copy');
            document.body.removeChild(textArea);
            if(!copied) {
                throw new Error('Your browser denied the copy request');
            }

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue copying your feedback link. ${(typeof(e) === 'string' ? e : e.message) || 'An unknown error occurred'}`
            })
        }
    }

    const onDownloadQRCode = async url => {
        try {

            // prepare a download link
            let link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            link.download = 'qr_code.png';
            document.body.appendChild(link);
            link.click();

            await Utils.sleep(0.25);
            document.body.removeChild(link);

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue downloading your feedback QR Code. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onEditClick = () => {
        if(!abstract.object.client_id) {
            utils.alert.show({
                title: 'Just a Second',
                message: 'This is a standard template meant for all Clients. You can customize your own template by creating a new template.',
                buttons: [{
                    key: 'new',
                    title: 'New Template',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {

                    if(key === 'new') {

                        let template = Feedback.Template.new();
                        template.client = utils.client.get();
                        template.client_id = template.client.id;

                        utils.layer.open({
                            id: `new_feedback_template`,
                            abstract: Abstract.create({
                                type: 'feedback_template',
                                object: template
                            }),
                            Component: AddEditFeedbackTemplate.bind(this, {
                                isNewTarget: true
                            })
                        });
                        return;
                    }
                }
            });
            return;
        }
        utils.layer.open({
            id: `edit_feedback_template_${abstract.getID()}`,
            abstract: abstract,
            Component: AddEditFeedbackTemplate.bind(this, {
                isNewTarget: false
            })
        })
    }

    const onGeneratePublicURL = () => {

        let tmpValue = null;
        utils.alert.show({
            title: 'Generate Feedback Link',
            message: 'Every feedback link is tied to a specific appointment. This allows us to give the proper credit when feedback is submitted with the link. Search for an appointment below to generate your feedback link.',
            content: (
                <div style={{
                    padding: 12,
                    width: '100%'
                }}>
                    <AppointmentLookupField
                    utils={utils}
                    placeholder={'Search by first or last name...'}
                    onChange={appointment => tmpValue = appointment} />
                </div>
            ),
            buttons: [{
                key: 'done',
                title: 'Create Link',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: async key => {
                if(key === 'done' && tmpValue) {
                    onGeneratePublicURLConfirm(tmpValue);
                    return;
                }
            }
        })
    }


    const onGeneratePublicURLConfirm = async appointment => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);
            let { qr_code, url } = await Request.get(utils, '/clients/', {
                type: 'public_feedback_url',
                template_id: abstract.getID(),
                appointment_id: appointment.id
            })

            setLoading(false);
            utils.alert.show({
                title: 'All Done!',
                message: `We have created the public url for "${abstract.object.title}". You can scan the QR Code below to open the feedback form or you can copy the url and share it manually`,
                content: (
                    <div style={{
                        padding: 12,
                        paddingTop: 0
                    }}>
                        <img
                        src={qr_code}
                        style={{
                            width: 250,
                            height: 250,
                            objectFit: 'contain',
                            borderRadius: 10,
                            overflow: 'hidden'
                        }} />
                    </div>
                ),
                buttons: [{
                    key: 'copy',
                    title: 'Copy URL',
                    style: 'default'
                },{
                    key: 'download',
                    title: 'Download QR Code',
                    style: 'default'
                }],
                onClick: key => {
                    if(key === 'copy') {
                        onCopyURL(url);
                        return;
                    }
                    if(key === 'download') {
                        onDownloadQRCode(qr_code);
                        return;
                    }
                }
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue creating your link for this feedback form. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const onOptionsClick = evt => {
        utils.sheet.show({
            items: [{
                key: 'active',
                title: `Change to ${abstract.object.active ? 'Inactive' : 'Active'}`,
                style: abstract.object.active ? 'destructive' : 'default'
            },{
                key: 'public_url',
                title: 'Generate Public URL',
                style: 'default'
            },{
                key: 'set_default',
                title: 'Set as Client Default',
                style: 'default',
                visible: abstract.object.is_default ? false : true
            }],
            target: evt.target
        }, key => {
            if(key === 'active') {
                onChangeActiveStatus();
                return;
            }
            if(key === 'public_url') {
                onGeneratePublicURL();
                return;
            }
            if(key === 'set_default') {
                onSetAsDefault();
                return;
            }
        })
    }

    const onSetAsDefault = () => {
        utils.alert.show({
            title: 'Set as Client Default',
            message: `Are you sure that you want to set this template as the default Client feedback form?`,
            buttons: [{
                key: 'confirm',
                title: 'Set as Deafult',
                style: 'default'
            },{
                key: 'cancel',
                title: 'Cancel',
                style: 'cancel'
            }],
            onClick: key => {
                if(key === 'confirm') {
                    onSetAsDefaultConfirm();
                    return;
                }
            }
        });
    }

    const onSetAsDefaultConfirm = async () => {
        try {
            setLoading(true);
            await Utils.sleep(0.25);

            await Request.post(utils, '/clients/', {
                type: 'set_feedback_template_default',
                id: abstract.getID()
            });

            setLoading(false);
            abstract.object.is_default = true;

            utils.content.fetch('feedback_template');
            utils.banner.show({
                title: 'Default Template Set',
                message: `This template has been set as the default feedback template for the Client`
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the active status for this template. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const getButtons = () => {
        return [{
            key: 'options',
            text: 'Options',
            color: 'secondary',
            onClick: onOptionsClick
        },{
            key: 'edit',
            text: 'Edit',
            color: 'primary',
            onClick: onEditClick
        }];
    }

    const getFields = () => {

        let template = abstract.object;
        let items = [{
            key: 'details',
            title: 'Details',
            items: [{
                key: 'id',
                title: 'ID',
                value: template.id
            },{
                key: 'title',
                title: 'Title',
                value: template.title
            },{
                key: 'description',
                title: 'Description',
                value: template.description
            },{
                key: 'client',
                title: 'Client',
                visible: utils.user.get().level <= User.levels.get().admin,
                value: template.client ? template.client.name : 'All Clients'
            },{
                key: 'is_default',
                title: 'Default for Client',
                value: template.is_default ? 'Yes' : 'No'
            },{
                key: 'active',
                title: 'Active',
                value: template.active ? 'Yes' : 'No'
            },{
                button: {
                    color: 'primary',
                    label: 'Generate',
                    onClick: onGeneratePublicURL
                },
                component: 'button',
                key: 'url',
                title: 'Public URL'
            }]
        }]

        return items;
    }

    const getURL = () => {
        return `${API.server}/feedback/index.html?template_id=${abstract.getID()}&preview=1&v=${moment().unix()}&client_id=${utils.client.get().id}`
    }

    useEffect(() => {
        setURL(getURL());
        utils.content.subscribe(layerID, ['feedback_templates'], {
            onFetch: () => setURL(getURL()),
            onUpdate: () => setURL(getURL())
        });
        return () => {
            utils.content.unsubscribe(layerID);
        }
    }, [])

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={abstract.getTitle()}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading,
            sizing: 'medium'
        }}>
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                marginBottom: 20
            }}>
                <div style={{
                    height: 350
                }}>
                    <iframe
                    className={'template-preview'}
                    src={url}
                    style={{
                        width: '100%',
                        height: 350,
                        border: 'none',
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                        borderBottom: `1px solid ${Appearance.colors.divider()}`
                    }} />
                </div>
                <div style={{
                    padding: 12,
                    textAlign: 'center'
                }}>
                    <span
                    className={'text-button'}
                    onClick={() => window.open(getURL())}
                    style={{
                        ...Appearance.textStyles.title(),
                        color: Appearance.colors.primary()
                    }}>{'Open Preview in New Window'}</span>
                </div>
            </div>
            <FieldMapper 
            sections={getFields()} 
            utils={utils} />
        </Layer>
    )
}

export const JitsiMeeting = ({ token }, { abstract, index, options, utils }) => {

    const layerID = `jitsi_meeting_${abstract.object.meeting_id}`;
    const [api, setApi] = useState(null);
    const [audioEnabled, setAudioEnabled] = useState(false);
    const [authToken, setAuthToken] = useState(token);
    const [layerState, setLayerState] = useState(null);
    const [loading, setLoading] = useState(token ? false : true);
    const [screenShareEnabled, setScreenShareEnabled] = useState(false);
    const [videoEnabled, setVideoEnabled] = useState(false);

    const onAudioStateChange = ({ muted }) => {
        setAudioEnabled(muted ? false : true);
    }

    const onCloseLayer = () => {
        console.log('closing layer');
        api?.executeCommand('hangup');
    }

    const onReadyToClose = () => {
        setLayerState('close');
    }

    const onScreenShareChange = ({ on }) => {   
        setScreenShareEnabled(on);
    }

    const onVideoStateChange = ({ muted }) => {
        setVideoEnabled(muted ? false : true);
    }

    const getContent = () => {
        if(!authToken || loading === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: '100%',
                    width: '100%'
                }}>
                    <LottieView
                    autoPlay={true}
                    loop={true}
                    source={window.theme === 'dark' ? require('files/lottie/dots-white.json') : require('files/lottie/dots-grey.json')}
                    style={{
                        height: 50,
                        width: 50
                    }}/>
                </div>
            )
        }
        return (
            <div style={{
                height: '100%',
                width: '100%'
            }}>
                <JitsiMeetingComponent
                configOverwrite={{
                    defaultLanguage: 'en',
                    disableDeepLinking: true,
                    enableLobby: true,
                    filmstrip: {
                        disableResizable: true
                    },
                    startWithAudioMuted: true,
                    startWithVideoMuted: true,
                    prejoinConfig: {
                        enabled: true,
                        hideDisplayName: false
                    }
                }}
                domain={'jitsi.life-wipe.com'}
                getIFrameRef={iframeRef => { 
                    iframeRef.style.height = '100%';
                    iframeRef.style.width = '100%';
                }}
                interfaceConfigOverwrite={{
                    APP_NAME: 'LifeWipe',
                    DEFAULT_LOCAL_DISPLAY_NAME: utils.user.get().full_name,
                    TOOLBAR_BUTTONS: [],
                }}
                jwt={authToken}
                onApiReady={setApi}
                roomName={abstract.object.meeting_id}
                style={{
                    height: '100%',
                    width: '100%'
                }}
                userInfo={{
                    displayName: utils.user.get().full_name
                }} />
                {getControls()}
            </div>
        )
    }

    const getControls = () => {
        return (
            <div style={{
                bottom: 20,
                display: 'flex',
                justifyContent: 'center',
                left: 0,
                gap: 12,
                position: 'absolute',
                right: 0
            }}>
                <img 
                className={'cursor-pointer'}
                onClick={() => api.executeCommand('hangup')}
                src={'images/jitsi-leave-meeting-red.png'}
                style={{
                    height: 50,
                    width: 50
                }} />
                <img 
                className={'cursor-pointer'}
                onClick={() => api.executeCommand('toggleShareScreen')}
                src={screenShareEnabled ? 'images/jitsi-screen-share-enabled.png' : 'images/jitsi-screen-share-disabled.png'}
                style={{
                    height: 50,
                    width: 50
                }} />
                <img 
                className={'cursor-pointer'}
                onClick={() => api.executeCommand('toggleAudio')}
                src={audioEnabled ? 'images/jitsi-audio-enabled.png' : 'images/jitsi-audio-disabled.png'}
                style={{
                    height: 50,
                    width: 50
                }} />
                <img 
                className={'cursor-pointer'}
                onClick={() => api.executeCommand('toggleVideo')}
                src={videoEnabled ? 'images/jitsi-video-enabled.png' : 'images/jitsi-video-disabled.png'}
                style={{
                    height: 50,
                    width: 50
                }} />
            </div>
        );
    }

    const fetchToken = async () => {
        try {

            // submit request to server to generate host token
            let { token } = await Request.get(utils, '/appointments/', {
                category: 'host',
                id: abstract.getID(),
                type: 'generate_meeting_url',
            });

            // end loading and update local state with token
            setLoading(false);
            setAuthToken(token);

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue starting this meeting. ${e.message || 'An unknown error occurred'}`,
                onClick: setLayerState.bind(this, 'close')
            })
        }
    }

    useEffect(() => {

        if(api) {

            api.addListener('audioMuteStatusChanged', onAudioStateChange);
            api.addListener('readyToClose', onReadyToClose);
            api.addListener('screenSharingStatusChanged', onScreenShareChange);
            api.addListener('videoMuteStatusChanged', onVideoStateChange);
            
            return () => {
                api.removeListener('audioMuteStatusChanged', onAudioStateChange);
                api.removeListener('readyToClose', onReadyToClose);
                api.removeListener('screenSharingStatusChanged', onScreenShareChange);
                api.removeListener('videoMuteStatusChanged', onVideoStateChange);
            }
        }
    }, [api]);

    useEffect(() => {
        if(!token) {
            fetchToken();
        }
    }, []);

    return (
        <Layer 
        id={layerID}
        index={index}
        title={`Meeting with ${abstract.object.lead.full_name}`}
        options={{
            ...options,
            onCloseLayer: onCloseLayer,
            layerState,
            removePadding: true,
            sizing: 'fullscreen'
        }}
        utils={utils}>
            {getContent()}
        </Layer>
    );
}

export const SetAppointmentAssignment = ({ abstract, index, options, utils }) => {

    const layerID = `set_appointment_assignment_${abstract.getID()}`;
    const [layerState, setLayerState]= useState(null);
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState(null);

    const onChangeUser = async () => {
        try {

            if(!user) {
                utils.alert.show({
                    title: 'Just a Second',
                    message: 'Please choose a user before moving on'
                });
                return;
            }
            
            setLoading('done');
            await Utils.sleep(0.25);

            let { status } = await Request.post(utils, '/appointments/', {
                type: 'set_assignment',
                id: abstract.getID(),
                user_id: user.user_id
            });

            setLoading(false);
            abstract.object.primary = user;
            abstract.object.status = status;
            utils.content.update(abstract);

            utils.banner.show({
                title: 'Assignment Changed',
                message: `The primary assignment for this appointment has been changed to ${user.full_name}`,
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating this appointment. ${e.message || 'An unknown error occurred'}`
            })
        }
    }

    const getButtons = () => {
        return [{
            color: user ? 'primary' : 'dark',
            key: 'done',
            loading: loading === 'done',
            onClick: onChangeUser,
            text: 'Submit'
        }];
    }

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={`${abstract.object.primary ? 'Change' : 'Assign'} Appointment Closer`}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            <LayerItem
            collapsed={false}
            title={'About Assignments'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    padding: '12px 16px 12px 16px',
                    width: '100%'
                }}>
                    <span style={{
                        color: Appearance.colors.text(),
                        display: 'block',
                        fontWeight: 500,
                        fontSize: 14,
                        width: '100%'
                    }}>{'The closer for an appointment is the user that is considered the main point of contact for the appointment. This user will receive notifications about the appointment and will be responsible for meeting with the customer and closing the sale.'}</span>   
                </div>
            </LayerItem>
            <LayerItem
            collapsed={false}
            lastItem={true}
            title={'User Assignment'}>
                <div style={{
                    ...Appearance.styles.unstyledPanel(),
                    padding: 16,
                    width: '100%'
                }}>
                    <UserLookupField
                    onChange={setUser}
                    placeholder={'Search for a user...'}
                    value={user}
                    utils={utils} />
                </div>
            </LayerItem>
        </Layer>
    )
}

export const SetAppointmentStatus = ({ abstract, index, options, utils }) => {

    const layerID = `set_appointment_status_${abstract.getID()}`;
    const [loading, setLoading] = useState(false);
    const [layerState, setLayerState] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [items, setItems] = useState([]);

    const onSetStatus = () => {

        // check for matching status codes for Appointments with customer feedback templates
        // request confirmation before sending customer feedback email
        
        let codes = Appointment.status.get();
        if(abstract.object.feedback_template && [codes.did_not_sell, codes.sale, codes.turndown].includes(selectedStatus)) {

            utils.alert.show({
                title: 'Confirm New Status',
                message: `Would you like to send the "${abstract.object.feedback_template.title}" questionnaire to the customer after updating the status?`,
                buttons: [{
                    key: 'do_not_send',
                    title: 'Only Update Status',
                    style: 'default'
                },{
                    key: 'confirm',
                    title: 'Update Status and Send Questionnaire',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: key => {
                    if(key !== 'cancel') {
                        onSetStatusConfirm(key === 'confirm');
                        return;
                    }
                }
            });
            return;
        }
        onSetStatusConfirm();
    }

    const onSetStatusConfirm = async questionnaire => {
        try {
            setLoading('done');
            await Utils.sleep(0.25);

            // send request to server
            let { status } = await Request.post(utils, '/appointments/', {
                type: 'set_status',
                id: abstract.getID(),
                status: selectedStatus,
                send_questionnaire: questionnaire
            })

            // end loading and update status for abstract target
            setLoading(false);
            abstract.object.status = status;

            // noptify subscribers that status has changed
            utils.content.update({
                object: {
                    id: abstract.getID(),
                    status: status
                },
                type: 'appointment_status'
            });

            // show user confirmation alert
            utils.banner.show({
                title: 'Status Updated',
                message: 'The status for this appointment has been updated',
                onShow: setLayerState.bind(this, 'close')
            });

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue updating the status for this appointment. ${e.message || 'An unknown error occurred'}`,
            })
        }
    }

    const getButtons = () => {
        return [{
            color: selectedStatus ? 'primary' : 'dark',
            key: 'done',
            loading: loading === 'done',
            onClick: onSetStatus,
            text: 'Save Changes'
        }];
    }

    const setupStatusCodes = () => {
        let codes = utils.client.status_codes.get().filter(status => {
            return status.capabilities.appointments;
        }).map(status => ({
            id: status.code,
            title: status.text
        }));
        setItems(codes);
    }

    useEffect(() => {
        setupStatusCodes();
    }, []);

    return (
        <Layer
        buttons={getButtons()}
        id={layerID}
        index={index}
        title={'Set Appointment Status'}
        utils={utils}
        options={{
            ...options,
            layerState: layerState,
            loading: loading === true,
            sizing: 'medium'
        }}>
            <select
            className={`custom-select ${window.theme}`}
            defaultValue={'Choose a status...'}
            onChange={e => {
                let id = Utils.attributeForKey.select(e, 'id');
                setSelectedStatus(parseInt(id));
            }}
            style={{
                background: `${Appearance.colors.textField()} url("images/custom-select-list-arrows-${window.theme === 'dark' ? 'white' : 'black'}.png") no-repeat right 0.75rem center`,
                backgroundSize: '10px',
                width: '100%'
            }}>
                <option disabled={true}>{'Choose a status...'}</option>
                {items.map((item, index) => {
                    return (
                        <option key={index} id={item.id}>{item.title}</option>
                    )
                })}
            </select>
        </Layer>
    )
}

// Components
export const getAppointmentStatus = (utils, appointment, options = {}) => {

    // prevent moving forward if no appointment status was provided
    if(!appointment || !appointment.status) {
        return null;
    }

    // prepare status click handler
    const onStatusClick = evt => {

        // prevent root component from triggering click event
        evt.stopPropagation();

        // only allow editing if options object does not contain a false editing flag
        if(options.editable !== false) {
            utils.layer.open({
                abstract: Abstract.create({
                    object: appointment,
                    type: 'appointments'
                }),
                Component: SetAppointmentStatus,
                id: `set_appointment_status_${appointment.id}`,
                permissions: ['appointments.actions.edit']
            });
        }
    }

    return (
        <div
        className={options.editable === false ? '' : 'text-button'}
        onClick={onStatusClick}
        style={{
            alignItems: 'center',
            background: Appearance.colors.softGradient(appointment.status.color),
            border: `2px solid ${appointment.status.color}`,
            borderRadius: 5,
            display: 'flex',
            flex: 'none',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'center',
            maxWidth: 125,
            overflow: 'hidden',
            paddingLeft: 8,
            paddingRight: 8,
            textAlign: 'center',
            width: '100%'
        }}>
            <span style={{
                ...Appearance.textStyles.subTitle(),
                color: 'white',
                fontWeight: '600',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%'
            }}>{appointment.status.text}</span>
        </div>
    )
}
